import * as api from '../../requests'
import { BUSINESS_RULES } from './businessRules.js'
import store, { resolveFieldEngine, resolveFieldLogic } from '../../store.js'
import { getUploadsFromFields } from './fieldComponents/UploadAccumulator'
import { testRepeaterConstraints, getHydratedFields } from 'utilities'
import { validateRepeaterConstraints } from './repeaterConstraints'
import { resolveFieldConditions } from '@tecuity/wizard-utilities'
import startOfDay from 'date-fns/startOfDay'
import differenceInDays from 'date-fns/differenceInDays'
import differenceInMonths from 'date-fns/differenceInMonths'
import isAfter from 'date-fns/isAfter'
import isBefore from 'date-fns/isBefore'
import isValid from 'date-fns/isValid'
import { isCountryUSA, ProcessingStatusIds } from '../../Utilities/Utilities.js'
import { getAddressObject } from 'components/Wizard2/WizardClasses'
import { setSuccessToast } from '../../actions.js'

/*
This file contains validation objects for validating field inputs.
These validators can be used for both inline, tab-off, and server validation.
To define a new validator, adhere to the following function structure:

[Validator Name]: ([Field Value], [Field Type], [{Object containing properties needed by the validation function}]) => {
  return ([Boolean: TRUE for passed validation, FALSE for failed validation], [optional string: A human-readable error message to be returned to the user.])
}

As described above, validators should always be pure functions, and should only return
either [true] or [false] with an optional error message if applicable. If you intend the
validation to transform the input value in some way, use the Mask() object.

Mask() functions may call Validate() functions, but Validate() functions should never
call Mask() functions. This ensures that when calling a Validate() function you may always
safely expect a boolean response.

*/

const valueIsNotEmptyOrNull = (value) =>
  (value !== '' && value !== null && value !== undefined && typeof value !== 'object' && !(/^\s*$/.test(value))) ||
  value === 0 ||
  value === '0'

const valueIsPossiblyValidZip = (value) => {
  try {
    return valueIsNotEmptyOrNull(value) && String(value).length >= 5
  } catch (e) {
    console.warn(e)
    return false
  }
}

const sanitizeNumber = (value) => {
  let returnVal
  returnVal = value.toString()
  if (returnVal.includes('.') && returnVal.split('.').length <= 2) {
    try {
      returnVal = Number.parseFloat(
        value.toString().replace(/,/g, '').replace(/\$/g, '').replace(/ /g, ''),
        10
      )
    } catch (e) {
      returnVal = value
    } finally {
      return returnVal
    }
  } else {
    try {
      returnVal = Number.parseInt(
        value.toString().replace(/,/g, '').replace(/\$/g, '').replace(/ /g, ''),
        10
      )
    } catch (e) {
      returnVal = value
    } finally {
      return returnVal
    }
  }
}

const getTrimmedValue = (value = '') => (!value ? '' : value.trim())

const valueIsANumber = (value) => !!/^-?[\d.]+(?:e-?\d+)?$/.test(value)

const emptyExceptDefaultState = (value, field) => {
  for (const key in value) {
    if (key != 'STATE' && key != 'COUNTRY' && valueIsNotEmptyOrNull(getTrimmedValue(value[key]))) {
      return false
    }
  }
  if (getTrimmedValue(value.STATE) === field.defaultState) {
    return true
  }
}

export const VALIDATE = {
  notEmptyOrNull: (value, field, props = {}, callback) => {
    //checks that the input is not empty.
    let testValue = value
    if (field.type === 'confirmFields') {
      testValue = value.val1
    }
    if (valueIsNotEmptyOrNull(testValue)) {
      callback(true, '')
    } else {
      callback(
        false,
        props.errorMessage && props.errorMessage !== ''
          ? props.errorMessage
          : 'This field is required.'
      )
    }
  },
  mustBeSelected: (value, field, props = {}, callback) => {
    const selectedOptionExists = field.options.find(
      (option) => option.value.toString() === value.toString()
    )
    if (value === 'default' || !selectedOptionExists) {
      callback(
        false,
        props.errorMessage && props.errorMessage !== ''
          ? props.errorMessage
          : 'This field is required.'
      )
    } else {
      callback(true, '')
    }
  },
  numberLessThan: (value, field, props = {}, callback) => {
    if (value) {
      //Checks that a given number is less than, but not equal to, a number provided on the "max" property of the props object.
      let sanitizedNumber = sanitizeNumber(value)
      if (!valueIsANumber(sanitizedNumber)) {
        callback(false, 'Please provide a valid number to continue.')
      } else if (sanitizedNumber <= props.max) {
        callback(true, '')
      } else {
        callback(
          false,
          props.errorMessage && props.errorMessage !== ''
            ? props.errorMessage
            : 'This number must be less than ' + props.max + '.'
        )
      }
    } else {
      callback(null, '')
    }
  },
  numberGreaterThan: (value, field, props = {}, callback) => {
    if (value) {
      let sanitizedNumber = sanitizeNumber(value)
      if (!valueIsANumber(sanitizedNumber)) {
        callback(false, 'Please provide a valid number to continue.')
      } else if (sanitizedNumber >= props.min) {
        callback(true, '')
      } else {
        callback(
          false,
          props.errorMessage && props.errorMessage !== ''
            ? props.errorMessage
            : 'This number must be greater than ' + props.min + '.'
        )
      }
    } else {
      callback(null, '')
    }
  },
  mustBeTrue: (value, field, props = {}, callback) => {
    //Checks that the given value is true. This is a "truthy" check, so the value may be either a string or boolean true.
    //You may include a custom error message on the props object
    if (value === true || value === 'true') {
      callback(true, '')
    } else {
      callback(
        false,
        props.errorMessage && props.errorMessage !== ''
          ? props.errorMessage
          : 'This field must be checked'
      )
    }
  },
  valuesMatch: (value, field, props = {}, callback) => {
    //Checks that two given values match each other. The value should be an object with 'val1' and 'val2' properties.
    //This is not a truthy check. The datatypes of the two values MUST match.
    //By default this validator is case-sensitive, but you can pass "caseSensitive: false" on the props parameter to deactivate the case-sensitivity
    let newValue = { ...value }
    newValue.val1 = getTrimmedValue(value.val1)
    newValue.val2 = getTrimmedValue(value.val2)
    if (props.caseSensitive === false) {
      newValue.val1 = newValue.val1.toLowerCase()
      newValue.val2 = newValue.val2.toLowerCase()
    }
    if (newValue.val1 === newValue.val2) {
      callback(true, '')
    } else {
      callback(
        false,
        props.errorMessage && props.errorMessage !== ''
          ? props.errorMessage
          : 'These two fields must match exactly'
      )
    }
  },
  mustNotEqualString: (value, field, props = {}, callback) => {
    //Checks that the given value does not equal a given string. The string must be passed on the props object under the "string" property
    //Because this validator is so dynamic, it is recommended that you pass in a custom errorMessage on the "errorMessage" property of the props object
    //By default this validator is case-sensitive, but you can pass "caseSensitive: false" on the props parameter to deactivate the case-sensitivity
    value = value.trim()
    if (props.caseSensitive === false) {
      value = value.toLowerCase()
    }
    if (value === props.string) {
      callback(false, props.errorMessage || props.string + ' is not a valid input.')
    } else {
      callback(true, '')
    }
  },
  mailingAddressIsRequired: (value, field, props = {}, callback) => {
    VALIDATE.addressIsRequired(value.MAIL_ADDR, field, props, callback)
  },
  addressIsRequired: (value, field, props = {}, callback) => {
    const isForeign = !isCountryUSA(value.COUNTRY)
    const requiresCounty = field.showCounty === true ? true : false
    if (requiresCounty) {
      if (valueIsNotEmptyOrNull(getTrimmedValue(value.COUNTY))) {
        callback(true, '')
      } else {
        callback(false, props.errorMessage || 'County is required')
      }
    }
    if (isForeign) {
      if (valueIsNotEmptyOrNull(getTrimmedValue(value.ADDR1))) {
        callback(true, '')
      } else {
        callback(false, props.errorMessage || 'Address 1 is required')
      }
    } else {
      if (valueIsNotEmptyOrNull(getTrimmedValue(field.defaultState)) && !value.STATE) {
        value.STATE = field.defaultState
      }

      if (
        valueIsNotEmptyOrNull(getTrimmedValue(value.ADDR1)) &&
        valueIsNotEmptyOrNull(getTrimmedValue(value.CITY)) &&
        valueIsNotEmptyOrNull(getTrimmedValue(value.STATE)) &&
        valueIsPossiblyValidZip(getTrimmedValue(value.POSTAL_CODE))
      ) {
        callback(true, '')
      } else {
        callback(false, props.errorMessage || 'Please provide a full address to continue.')
      }
    }
  },
  mailingAddressIsValid: (value, field, props = {}, callback) => {
    VALIDATE.addressIsValid(value.MAIL_ADDR, field, props, callback)
  },
  addressIsValid: (value, field, props = {}, callback) => {
    let isCompleteAddress =
      valueIsNotEmptyOrNull(getTrimmedValue(value.ADDR1)) &&
      ((valueIsNotEmptyOrNull(getTrimmedValue(value.CITY)) &&
        valueIsNotEmptyOrNull(getTrimmedValue(value.STATE))) ||
        valueIsNotEmptyOrNull(getTrimmedValue(value.POSTAL_CODE)))

    let isModified =
      valueIsNotEmptyOrNull(getTrimmedValue(value.ADDR1)) ||
      valueIsNotEmptyOrNull(getTrimmedValue(value.CITY)) ||
      valueIsNotEmptyOrNull(getTrimmedValue(value.POSTAL_CODE))

    const stateNotReplaceable = field.defaultState && field.disableState

    if (isCountryUSA(value.COUNTRY)) {
      if (!isCompleteAddress && isModified) {
        callback(false, props.errorMessage || 'Please provide a complete address.', {
          value: { ...value },
        })
      } else if (!isModified) {
        callback(null, 'Please provide an address.', {
          value: { ...value },
          confirmed: true,
        })
      } else {
        api.VALIDATE(
          'address',
          value,
          (data) => {
            if (data.valid) {
              const correctedState = (data.payload.corrected || {}).STATE
              if (field.disableState && value.STATE !== correctedState) {
                callback(
                  false,
                  `Provided ZIP code is not valid in the selected state of ${value.STATE}`,
                  {
                    value: {
                      ...value,
                      ...data.payload.corrected,
                      STATE: stateNotReplaceable ? value.STATE : data.payload.corrected.STATE,
                    },
                    infoMessage: data.payload.feedback.join('\n'),
                  }
                )
                if (!!data.payload.feedback) {
                  store.dispatch(setSuccessToast(data.payload.feedback.join('\n')))
                }
              } else {
                callback(true, '', {
                  value: {
                    ...value,
                    ...data.payload.corrected,
                    STATE: stateNotReplaceable ? value.STATE : data.payload.corrected.STATE,
                  },
                  infoMessage: data.payload.feedback.join('\n'),
                })
                if (!!data.payload.feedback) {
                  store.dispatch(setSuccessToast(data.payload.feedback.join('\n')))
                }
              }
            } else {
              callback(false, data.error, {
                value: {
                  ...value,
                  ...data.payload.corrected,
                  STATE: stateNotReplaceable ? value.STATE : data.payload.corrected.STATE,
                },
                infoMessage: null,
              })
            }
          },
          (err) => {
            callback(
              false,
              'Something went wrong while validating your address. If this problem persists, please contact customer support.'
            )
          }
        )
      }
    } else {
      if (valueIsNotEmptyOrNull(getTrimmedValue(value.ADDR1))) {
        callback(true, '')
      } else {
        callback(false, 'Please provide an address')
      }
    }
  },
  addressIsValidNew: (value, field, props = {}, callback) => {
    let isCompleteAddress =
      valueIsNotEmptyOrNull(getTrimmedValue(value.AddressLine1)) &&
      ((valueIsNotEmptyOrNull(getTrimmedValue(value.City)) &&
        valueIsNotEmptyOrNull(getTrimmedValue(value.StateOrProvince))) ||
        valueIsNotEmptyOrNull(getTrimmedValue(value.PostalCode)))

    let isModified =
      valueIsNotEmptyOrNull(getTrimmedValue(value.AddressLine1)) ||
      valueIsNotEmptyOrNull(getTrimmedValue(value.City)) ||
      valueIsNotEmptyOrNull(getTrimmedValue(value.PostalCode))

    const stateNotReplaceable = field.defaultState && field.disableState

    if (value.Country === 'United States') {
      if (!isCompleteAddress && isModified) {
        callback(false, props.errorMessage || 'Please provide a complete address.')
      } else if (!isModified) {
        callback(null, '', {
          value: { ...value },
          confirmed: true,
        })
      } else {
        api.VALIDATE(
          'address',
          value,
          (data) => {
            if (data.valid) {
              const correctedState = (data.payload.corrected || {}).StateOrProvince
              if (field.disableState && value.StateOrProvince !== correctedState) {
                callback(
                  false,
                  `Provided ZIP code is not valid in the selected state of ${value.StateOrProvince}`,
                  {
                    value: {
                      ...value,
                      ...data.payload.corrected,
                      StateOrProvince: stateNotReplaceable
                        ? value.StateOrProvince
                        : data.payload.corrected.StateOrProvince,
                    },
                    infoMessage: data.payload.feedback.join('\n'),
                  }
                )
              } else {
                callback(true, '', {
                  value: {
                    ...value,
                    ...data.payload.corrected,
                    StateOrProvince: stateNotReplaceable
                      ? value.StateOrProvince
                      : data.payload.corrected.StateOrProvince,
                  },
                  infoMessage: data.payload.feedback.join('\n'),
                })
              }
            } else {
              callback(false, data.error, {
                value: {
                  ...value,
                  ...data.payload.corrected,
                  StateOrProvince: stateNotReplaceable
                    ? value.StateOrProvince
                    : data.payload.corrected.StateOrProvince,
                },
                infoMessage: null,
              })
            }
          },
          (err) => {
            callback(
              false,
              'Something went wrong while validating your address. If this problem persists, please contact customer support.'
            )
          }
        )
      }
    } else {
      callback(true, '')
    }
  },
  partialMailingAddressNotAllowed: (value, field, props = {}, callback) => {
    VALIDATE.partialAddressNotAllowed(value.MAIL_ADDR, field, props, callback)
  },
  partialAddressNotAllowed: (value, field, props = {}, callback) => {
    const addr1IsEmpty = {
      value: !valueIsNotEmptyOrNull(getTrimmedValue(value.ADDR1)),
      name: 'Address 1',
    }
    const cityIsEmpty = { value: !valueIsNotEmptyOrNull(getTrimmedValue(value.CITY)), name: 'City' }
    const stateIsEmpty = {
      value: !valueIsNotEmptyOrNull(getTrimmedValue(value.STATE)),
      name: 'State',
    }
    const postalIsEmpty = {
      value: !valueIsNotEmptyOrNull(getTrimmedValue(value.POSTAL_CODE)),
      name: 'ZIP code',
    }
    const anyEmpty =
      addr1IsEmpty.value || cityIsEmpty.value || stateIsEmpty.value || postalIsEmpty.value
    const allEmpty =
      addr1IsEmpty.value && cityIsEmpty.value && stateIsEmpty.value && postalIsEmpty.value
    // If there's a default state, consider the address empty/valid.
    if (emptyExceptDefaultState(value, field)) {
      callback(null, '')
    } else if (isCountryUSA(value.COUNTRY)) {
      if (allEmpty) {
        callback(null, '')
      } else if (!anyEmpty) {
        callback(true, '')
      } else {
        let fields = [addr1IsEmpty, cityIsEmpty, stateIsEmpty, postalIsEmpty]
        let numEmpties = 0
        fields.forEach((field) => {
          if (field.value) {
            numEmpties++
          }
        })
        let empties = fields.filter((field) => field.value)

        if (numEmpties > 2) {
          let error = ''
          empties.forEach((empty, i) => {
            error = `${error}${
              i === empties.length - 1 ? 'and ' + empty.name + ' are required.' : empty.name + ', '
            }`
          })
          callback(false, props.errorMessage || `Please provide a complete address, ${error}`)
        } else if (numEmpties === 2) {
          callback(
            false,
            props.errorMessage ||
              `Please provide a complete address, ${empties[0].name} and ${empties[1].name} are required.`
          )
        } else if (numEmpties === 1) {
          callback(
            false,
            props.errorMessage ||
              `Please provide a complete address, ${empties[0].name} is required.`
          )
        } else {
          callback(true, '')
        }
      }
    } else {
      //Foreign
      if (addr1IsEmpty.value) {
        callback(false, `Please provide a value in Address 1`)
      } else {
        callback(true, '')
      }
    }
  },
  registeredAgentAddressIsValid: (value, field, props = {}, callback) => {
    let messages = []
    let payload = { addressCorrected: false }

    if (value.TYPE_ID.toString() === '2' && value.CRA_ID.toString() === '0' && field.addingAgent) {
      const validatePrincipleAgainstAMS =
        props.principalAddressAMS || process.env.RA_PRINCIPAL_ADDRESS_AMS
      const validateOrgAddress = !!props.organizationAddressRequired
      let isIndividual = value.ORG_YN === false
      let hasInputFirstAndLastNames =
        valueIsNotEmptyOrNull(value.FIRST_NAME) && valueIsNotEmptyOrNull(value.LAST_NAME)
      let hasSelectedOrganization = value.ORGANIZATION
        ? value.ORGANIZATION !== null && value.ORGANIZATION.SOURCE_ID !== 0
        : false
      let hasInputAddress =
        valueIsNotEmptyOrNull(getTrimmedValue(value.ADDR1)) &&
        valueIsNotEmptyOrNull(getTrimmedValue(value.CITY)) &&
        valueIsNotEmptyOrNull(getTrimmedValue(value.STATE)) &&
        valueIsNotEmptyOrNull(getTrimmedValue(value.POSTAL_CODE))

      if (isIndividual) {
        if (!hasInputFirstAndLastNames) {
          messages.push(
            props.errorMessage ||
              'Please provide the first and last name of the registered agent to continue.'
          )
          payload = { ...payload, validAgentName: false }
        } else {
          payload = { ...payload, validAgentName: true }
        }
        if (!hasInputAddress) {
          messages.push(props.errorMessage || 'Please enter a complete physical address.')
          payload = { ...payload, validAddress: false }
        }
        if (validatePrincipleAgainstAMS && hasInputAddress) {
          let promise = new Promise((resolve) => {
            VALIDATE.addressIsValid(value, field, props, (isValid, error, payload) => {
              resolve({ isValid, error, payload })
            })
          })
          promise
            .then((address) => {
              let principlePayload = address.payload
              if (principlePayload && principlePayload.value) {
                payload = { ...payload, addressCorrected: true }
                const newState = principlePayload.value.STATE
                payload = {
                  ...payload,
                  value: {
                    ...value,
                    ADDR1: principlePayload.value.ADDR1,
                    ADDR2: principlePayload.value.ADDR2,
                    ADDR3: principlePayload.value.ADDR3,
                    CITY: principlePayload.value.CITY,
                    STATE: principlePayload.value.STATE,
                    COUNTRY: principlePayload.value.COUNTRY,
                    COUNTY: principlePayload.value.COUNTY,
                    POSTAL_CODE: principlePayload.value.POSTAL_CODE,
                  },
                }
                if (value.STATE !== newState) {
                  messages.push(
                    `Provided ZIP code is not valid in the selected state of ${value.STATE}`
                  )
                  payload = { ...payload, validAddress: false }
                }
              }
              if (address.isValid !== true) {
                messages.push(
                  props.errorMessage || 'Physical address must be a valid address to continue.'
                )
                payload = { ...payload, validAddress: false }
              }
            })
            .then(() => {
              messages = [...new Set(messages)]
              callback(messages.length === 0, messages.join('\n\n'), payload)
            })
        } else {
          messages = [...new Set(messages)]
          callback(messages.length === 0, messages.join('\n\n'), payload)
        }
      } else if (!hasSelectedOrganization) {
        messages.push(props.errorMessage || 'Please select an Organization to continue.')
        payload = { ...payload, validAgentOrg: false }
        messages = [...new Set(messages)]
        callback(messages.length === 0, messages.join('\n\n'), payload)
      } else {
        if(validateOrgAddress){
          if (validatePrincipleAgainstAMS && hasInputAddress) {
            let promise = new Promise((resolve) => {
              VALIDATE.addressIsValid(value, field, props, (isValid, error, payload) => {
                resolve({ isValid, error, payload })
              })
            })
            promise
              .then((address) => {
                let principlePayload = address.payload
                if (principlePayload && principlePayload.value) {
                  payload = { ...payload, addressCorrected: true }
                  const newState = principlePayload.value.STATE
                  payload = {
                    ...payload,
                    value: {
                      ...value,
                      ADDR1: principlePayload.value.ADDR1,
                      ADDR2: principlePayload.value.ADDR2,
                      ADDR3: principlePayload.value.ADDR3,
                      CITY: principlePayload.value.CITY,
                      STATE: principlePayload.value.STATE,
                      COUNTRY: principlePayload.value.COUNTRY,
                      COUNTY: principlePayload.value.COUNTY,
                      POSTAL_CODE: principlePayload.value.POSTAL_CODE,
                    },
                  }
                  if (value.STATE !== newState) {
                    messages.push(
                      `Provided ZIP code is not valid in the selected state of ${value.STATE}`
                    )
                    payload = { ...payload, validAddress: false }
                  }
                }
                if (address.isValid !== true) {
                  messages.push(
                    props.errorMessage || 'Physical address must be a valid address to continue.'
                  )
                  payload = { ...payload, validAddress: false }
                }
              })
              .then(() => {
                messages = [...new Set(messages)]
                callback(messages.length === 0, messages.join('\n\n'), payload)
              })
          } else {
            messages = [...new Set(messages)]
            callback(messages.length === 0, messages.join('\n\n'), payload)
          }
        }else{
          payload = {
            ...payload,
            validAddress: true,
            validName: isIndividual ? true : null,
            validAgentOrg: isIndividual ? null : true,
          }
          messages = [...new Set(messages)]
          callback(messages.length === 0, messages.join('\n\n'), payload)
        }
      }
    } else {
      callback(true, '', payload)
    }
  },
  registeredAgentIsRequired: (value, field, props = {}, callback) => {
    let isNonCommercial = value.TYPE_ID.toString() === '2'
    let craId = value.CRA_ID.toString()
    let hasSelectedRa = craId !== '0' && craId !== '' && craId
    let isIndividual = value.ORG_YN === false
    let hasInputFirstAndLastNames =
      valueIsNotEmptyOrNull(value.FIRST_NAME) && valueIsNotEmptyOrNull(value.LAST_NAME)
    let hasSelectedOrganization = value.ORGANIZATION
      ? value.ORGANIZATION.SOURCE_ID !== 0 && value.ORGANIZATION !== null
      : false
    const regex = /(.{1,}?)@(.{1,}?)\.(.{1,})/g
    let hasInputValidEmail = process.env.RA_EMAIL
      ? valueIsNotEmptyOrNull(value.EMAIL) && regex.test(value.EMAIL)
      : true
    let hasInputAddress =
      valueIsNotEmptyOrNull(getTrimmedValue(value.ADDR1)) &&
      valueIsNotEmptyOrNull(getTrimmedValue(value.CITY)) &&
      valueIsNotEmptyOrNull(getTrimmedValue(value.STATE)) &&
      valueIsNotEmptyOrNull(getTrimmedValue(value.POSTAL_CODE))

    let isValid = null
    let errorMessage = []

    const setCallback = (valid, err) => {
      if (!valid) {
        isValid = valid
        errorMessage.push(err)
      } else if (isValid === null && valid === true) {
        isValid = valid
      }
    }

    if (isNonCommercial) {
      if (hasSelectedRa) {
        setCallback(true, '')
      } else if (isIndividual) {
        if (hasInputAddress && hasInputFirstAndLastNames && hasInputValidEmail) {
          setCallback(true, '')
        }
        if (!hasInputAddress) {
          setCallback(
            false,
            'Please provide the full physical address of the registered agent to continue.'
          )
        }
        if (!hasInputFirstAndLastNames) {
          setCallback(
            false,
            'Please provide the first and last names of the registered agent to continue.'
          )
        }
        if (field.addingAgent && !hasInputValidEmail) {
          setCallback(false, 'Input a valid email to continue.')
        }
      } else if (!isIndividual) {
        if (hasSelectedOrganization && hasInputValidEmail) {
          setCallback(true, '')
        }
        if (!hasSelectedOrganization) {
          setCallback(
            false,
            'Please provide the name of the registered agent organization to continue.'
          )
        }
        if (field.addingAgent && !hasInputValidEmail) {
          setCallback(false, 'Input a valid email to continue.')
        }
      } else {
        setCallback(
          false,
          'Select an existing registered agent or fill out the fields above to create a new registered agent before continuing.'
        )
      }
    } else {
      if (hasSelectedRa) {
        setCallback(true, '')
      } else {
        setCallback(false, props.errorMessage || 'Select a registered agent to continue.')
      }
    }
    callback(isValid, errorMessage.join('\n'))
  },
  registeredAgentMailingAddressIsValid: (value, field, props = {}, callback) => {
    let payload = { mailAddressCorrected: false }
    if (value.TYPE_ID.toString() === '2' && value.CRA_ID.toString() === '0' && field.addingAgent) {
      const validateMailingAgainstAMS = props.validateThruAMS
      const validateOrgAddress = !!props.organizationAddressRequired
      let isIndividual = value.ORG_YN === false
      let hasInputAddress =
        value.MAIL_ADDR &&
        valueIsNotEmptyOrNull(getTrimmedValue(value.MAIL_ADDR.ADDR1)) &&
        valueIsNotEmptyOrNull(getTrimmedValue(value.MAIL_ADDR.CITY)) &&
        valueIsNotEmptyOrNull(getTrimmedValue(value.MAIL_ADDR.STATE)) &&
        valueIsNotEmptyOrNull(getTrimmedValue(value.MAIL_ADDR.POSTAL_CODE))

      if (isIndividual || validateOrgAddress) {
        if (!hasInputAddress) {
          callback(false, props.errorMessage || 'Please enter a complete mailing address', {
            ...payload,
            validMailAddress: false,
          })
        } else if (validateMailingAgainstAMS) {
          let promise = new Promise((resolve) => {
            VALIDATE.addressIsValid(value.MAIL_ADDR, field, props, (isValid, error, payload) => {
              resolve({ isValid, error, payload })
            })
          })
          promise.then((address) => {
            let mailingPayload = address.payload
            if (mailingPayload && mailingPayload.value) {
              payload = { ...payload, mailAddressCorrected: true }
              const newState = mailingPayload.value.STATE
              payload = {
                ...payload,
                value: {
                  ...value,
                  MAIL_ADDR: {
                    ADDR1: mailingPayload.value.ADDR1,
                    ADDR2: mailingPayload.value.ADDR2,
                    ADDR3: mailingPayload.value.ADDR3,
                    CITY: mailingPayload.value.CITY,
                    STATE: mailingPayload.value.STATE,
                    COUNTRY: mailingPayload.value.COUNTRY,
                    COUNTY: mailingPayload.value.COUNTY,
                    POSTAL_CODE: mailingPayload.value.POSTAL_CODE,
                  },
                },
              }
              if (value.MAIL_ADDR.STATE !== newState) {
                callback(
                  false,
                  `Provided mailing ZIP code is not valid in the selected state of ${value.STATE}`,
                  { ...payload, validMailAddress: false }
                )
                return
              }
            }
            if (address.isValid) {
              callback(true, '', { ...payload, validMailAddress: true })
            } else {
              callback(
                false,
                props.errorMessage || 'Mailing address must be a valid address to continue.',
                { ...payload, validMailAddress: false }
              )
            }
          })
        } else {
          callback(true, '', { ...payload, validMailAddress: true })
        }
      } else {
        callback(true, '', { ...payload, validMailAddress: null })
      }
    } else {
      callback(true, '')
    }
  },
  registeredAgentNotSoS: (value, field, props = {}, callback) => {
    let craId = value.CRA_ID.toString()
    let sosAgentId = store.getState().app.appSettings['SOSAgentId']
    if (sosAgentId && craId === sosAgentId) {
      callback(
        false,
        'Secretary of State cannot be used as a registered agent. Select a different agent.'
      )
    } else {
      callback(true, '')
    }
  },
  checkAgentNameForRegulatedWords: (value, field, props = {}, callback) => {
    if (value.ORG_YN === false && (value.CRA_ID === 0 || value.CRA_ID === '0')) {
      const input = stringifyIndividualName(value)
      if (input === '') {
        callback(true, '', { validAgentName: null })
      } else {
        const promises = props.regulatedWordLists.map(
          (category) =>
            new Promise((resolve, reject) => {
              api.CHECK_FOR_REGULATED_WORDS(
                category,
                169,
                0,
                0,
                input,
                (response) => resolve(response),
                (err) => reject(err)
              )
            })
        )
        Promise.all(promises).then((responses) => {
          const isValid = responses.every((res) => res.valid !== false)
          const errorMessage = responses
            .filter((res) => res.error)
            .map((res) => res.error)
            .join('\n')
          const infoMessage = responses
            .filter((res) => res.payload.INFO_MESSAGE)
            .map((res) => res.payload.INFO_MESSAGE)
            .join('\n')
          const triggerInternalReview = responses.some((res) => res.payload.TRIGGER_INTERNAL_REVIEW)
          if (triggerInternalReview) {
            store.dispatch({
              type: 'ADD_INTERNAL_REVIEW_REQUIRED',
              fieldId: field.fieldId,
            })
          } else {
            store.dispatch({
              type: 'REMOVE_INTERNAL_REVIEW_REQUIRED',
              fieldId: field.fieldId,
            })
          }
          callback(isValid, errorMessage, { infoMessage, validAgentName: isValid })
        })
      }
    } else {
      callback(true, '', { validAgentName: null })
    }
  },
  noRegulatedWords: (value, field, props = {}, callback) => {
    //Test that a given value does not contain regulated words surrounded by whitespace.
    let metadata = store.getState().wizard.wizard.meta
    const filingTypeIdSrcField = store.getState().wizard.wizard.fields[props.filingTypeId]
    const filingTypeIdVal = filingTypeIdSrcField ? filingTypeIdSrcField.value : null
    const { FILING_TYPE_ID = 0 } = metadata
    const filingTypeId =
      filingTypeIdVal && filingTypeIdVal !== '0' ? filingTypeIdVal : FILING_TYPE_ID
    if (filingTypeId === 0) {
      callback(
        false,
        'NoRegulatedWords: Business rule failed because no FILING_TYPE_ID found in MetaData.'
      )
    } else if (value.val1 === '') {
      callback(false, 'Please provide a name')
    } else {
      api.VALIDATE(
        'RegulatedWords',
        {
          FILING_NAME: typeof value.val1 === 'string' ? value.val1 : value,
          FILING_TYPE_ID: filingTypeId,
        },
        (data) => {
          if (data.valid) {
            callback(true, '')
          } else {
            callback(false, data.error)
          }
        }
      )
    }
  },
  hasNameEnding: (value, field, props = {}, callback) => {
    //Tests that the given value contains the designation appropriate for the entity type. (LLC, LLP, CO, etc...)
    let metadata = store.getState().wizard.wizard.meta
    const filingTypeIdSrcField = store.getState().wizard.wizard.fields[props.filingTypeId]
    const filingTypeIdVal = filingTypeIdSrcField ? filingTypeIdSrcField.value : null
    const { FILING_TYPE_ID = 0 } = metadata
    const filingTypeId =
      filingTypeIdVal && filingTypeIdVal !== '0' ? filingTypeIdVal : FILING_TYPE_ID
    if (filingTypeId === 0) {
      callback(
        false,
        'HasNameEnding: Business rule failed because no FILING_TYPE_ID found in MetaData.'
      )
    } else {
      let payload = {}
      if (typeof value.val1 === 'string') {
        //If this is a confirm field
        payload = { FILING_NAME: value.val1, FILING_TYPE_ID: filingTypeId }
      } else {
        payload = { FILING_NAME: value, FILING_TYPE_ID: filingTypeId }
      }

      if (payload.FILING_NAME === '' || payload.FILING_TYPE_ID === 0) {
        callback(true, '')
      } else {
        api.VALIDATE('NameDesignation', payload, (data) => {
          if (data.valid) {
            callback(true, '')
          } else {
            callback(false, props.errorMessage ? props.errorMessage : data.error)
          }
        })
      }
    }
  },
  isValidName: (value, field, props = {}, callback) => {
    //Tests that the given value contains the designation appropriate for the entity type. (LLC, LLP, CO, etc...)
    let metadata = store.getState().wizard.wizard.meta
    const recordTypeIdSrcField = store.getState().wizard.wizard.fields[props.filingTypeId]
    const recordTypeIdVal = recordTypeIdSrcField ? recordTypeIdSrcField.value : null
    const paramFields = value.parameterFields.map(
      (subField) => store.getState().wizard.wizard.fields[subField]
    )
    const { RECORD_TYPE_ID = 0 } = metadata
    const recordTypeId =
      recordTypeIdVal && recordTypeIdVal !== '0' ? recordTypeIdVal : RECORD_TYPE_ID

    const bypassRecordTypeIdCheck =
      field.businessRules.find((r) => r.fn === 'validateEntityNameFields') &&
      field.businessRules.find((r) => r.fn === 'validateEntityNameFields').props
        .disableRecordTypeIdCheck === true

    if (recordTypeId === 0 && !bypassRecordTypeIdCheck) {
      callback(false, 'Business rule failed because no RECORD_TYPE_ID found in MetaData.')
    } else {
      let payload = {}
      const subRecordTypeIdSrcField = store.getState().wizard.wizard.fields[props.filingSubTypeId]
      const isProcessing = store.getState().router.location.pathname.includes('process')
      const subRecordTypeIdVal = subRecordTypeIdSrcField ? subRecordTypeIdSrcField.value : null
      if (typeof value.val1 === 'string') {
        //If this is a confirm field
        payload = {
          ...metadata,
          ADDITIONAL_PARAMS: {
            ...Object.fromEntries(
              paramFields.map((pf) => [
                [pf.name],
                typeof pf.value === 'object' ? JSON.stringify(pf.value) : pf.value,
              ])
            ),
            useConversionXref: props.useConversionXref,
            bypassRecordTypeIdCheck: bypassRecordTypeIdCheck,
          },
          FILING_NAME:
            field.hideReserved || value.reservedYN === 'N' ? value.val1 : value.reservedName,
          FIELD_NAME: field.name,
          RESERVED_FILING_ID: value.reservedNameId === '' ? null : value.reservedNameId,
          RECORD_TYPE_ID: recordTypeId,
          RECORD_SUBTYPE_ID: subRecordTypeIdVal === '' ? 0 : subRecordTypeIdVal || 0,
          ADDITIONAL_MODE: field.hideAdditionalName ? null : value.additionalMode || null,
          RESERVED_MODE: field.hideReserved ? null : value.reservedMode || null,
          STAFF_MODE: isProcessing,
        }
      } else {
        payload = {
          ...metadata,
          FILING_NAME: value,
          FIELD_NAME: field.name,
          RESERVED_FILING_ID: value.reservedNameId === '' ? null : value.reservedNameId,
          RECORD_TYPE_ID: recordTypeId,
          RECORD_SUBTYPE_ID: props.recordSubTypeId === '' ? 0 : props.recordSubTypeId || 0,
          ADDITIONAL_MODE: field.hideAdditionalName ? null : value.additionalMode,
          RESERVED_MODE: field.hideReserved ? null : value.reservedMode || null,
          STAFF_MODE: isProcessing,
        }
      }
      if (payload.FILING_NAME === '' || payload.FILING_TYPE_ID === 0) {
        callback(true, '', { infoMessage: '', requireSupportingDocument: false })
      } else {
        const validationType =
          (props.validationEndpointOverride || '') === ''
            ? 'ValidEntityName'
            : `ValidEntityName/${props.validationEndpointOverride}`
        api.VALIDATE(validationType, payload, (data) => {
          store.dispatch({
            type: 'SET_REQUIRE_SUPPORTING_DOC',
            fieldId: field.fieldId,
            requireSupportingDocument: data.requireSupportingDocument === true ? 'Y' : 'N',
          })
          if (data.valid) {
            callback(true, '', {
              infoMessage: data.payload.INFO_MESSAGE,
            })
          } else {
            callback(false, props.errorMessage ? props.errorMessage : data.error, {
              infoMessage: '',
            })
          }
        })
      }
    }
  },
  repeaterMinRows: (value, field, props = {}, callback) => {
    const numRows = field.trackChanges
      ? value.filter((row) => row._statusId !== 3).length
      : value.length
    if (numRows >= props.minRows) {
      callback(true, '')
    } else {
      if (props.noRowsIsValid && numRows === 0) {
        callback(null, '')
      } else {
        callback(
          false,
          props.errorMessage ||
            `You must add at least ${props.minRows} ${props.minRows === 1 ? 'entry' : 'entries'}.`
        )
      }
    }
  },
  repeaterMaxRows: (value, field, props = {}, callback) => {
    const numRows = field.trackChanges
      ? value.filter((row) => row._statusId !== 3).length
      : value.length
    if (numRows <= props.maxRows) {
      callback(true, '')
    } else {
      callback(false, props.errorMessage || `You may not add more than ${props.maxRows} entries.`)
    }
  },
  validateRepeaterFields: (value, field, props = {}, callback) => {
    const fields = store.getState().wizard.wizard.fields
    const secondaryValidation = store.getState().wizard.secondaryValidation
    const rowValids = value.map((row, rowIndex) => {
      const isExempt = props.skipDeletedRows ? row._statusId === 3 : false
      const { template } = field
      return new Promise((rowResolve) => {
        if (isExempt) {
          rowResolve(
            template.map((fieldId) => ({ isValid: null, valids: [], errors: [], fieldId }))
          )
        } else {
          const cellValids = template
            .map((fieldId) => {
              const hydratedFields = getHydratedFields(row, template, fields)
              const field = resolveFieldLogic(fields[fieldId], {
                fields: hydratedFields,
                currentRepeaterRowIndex: rowIndex,
              })
              const conditionsResolved = resolveFieldConditions(
                field.conditions,
                hydratedFields,
                field.conditionsOperator
              )
              if (conditionsResolved && field.active) {
                if (
                  secondaryValidation &&
                  !field.requireDoubleValidation &&
                  field.parent.type !== 'REPEATER'
                ) {
                  console.warn(
                    `double validation: disabled field business rules not run. fieldId: ${fieldId}`
                  )
                  return null
                }

                return new Promise((resolve) => {
                  loopThroughRules(
                    {
                      businessRules: field.businessRules,
                      value: row[fieldId],
                      field: { ...field, value: row[fieldId] },
                      event: 'onAdvance',
                      options: {
                        currentRepeaterRowIndex: rowIndex,
                      },
                    },
                    (validation) => {
                      resolve({ ...validation, fieldId: field.fieldId })
                    }
                  )
                })
              }
              return null
            })
            .filter((x) => x)
          Promise.all(cellValids).then((valids) => {
            rowResolve(valids)
          })
        }
      })
    })
    Promise.all(rowValids).then((valids) => {
      const fullValids = valids.map((row) =>
        row.reduce(
          (obj, cell) => ({
            ...obj,
            rowValid: obj.rowValid === false ? false : !cell.valids.includes(false),
            cellValids: {
              ...obj.cellValids,
              [cell.fieldId]: {
                isValid: cell.valids.every((valid) => valid !== false),
                errors: cell.errors,
              },
            },
          }),
          { rowValid: true, cellValids: {} }
        )
      )
      const allRowsValid = fullValids.every((row) => row.rowValid)
      if (allRowsValid) {
        callback(true, '', { rowErrors: null })
      } else {
        callback(false, 'Please review your entries for errors.', { rowErrors: fullValids })
      }
    })
  },
  validateThirdPartySignaturesRepeater: (value, field, props = {}, callback) => {
    if (value.method === 'signer') {
      VALIDATE.validateRepeaterFields(value.signers || [], field, props, callback)
    } else {
      callback(null, '')
    }
  },
  dateIsValid: (value, field, props, callback) => {
    // if date range
    if (value && value.start !== undefined) {
      if (!field.required && !value.start && !value.end) {
        callback(null, '')
        return
      }
      if (
        value.start &&
        isValid(new Date(value.start)) &&
        value.start.length >= 10 &&
        value.end &&
        isValid(new Date(value.end)) &&
        value.end.length >= 10
      ) {
        callback(true, '')
      } else {
        callback(false, 'Date format must be MM/DD/YYYY')
      }
    } // if standard date input
    else {
      if (!field.required && !value) {
        callback(null, '')
        return
      }
      if (
        (!value && field.businessRules.find((r) => r.fn === 'allowEmptyDate')) ||
        (value && isValid(new Date(value)) && value.length >= 10)
      ) {
        callback(true, '')
      } else {
        callback(false, 'Date format must be MM/DD/YYYY')
      }
    }
  },
  nameNotTaken: (value, field, props = {}, callback) => {
    if (value.val1 === '') {
      callback(true, '')
    } else {
      const RECORD_NUM = store.getState().wizard.wizard.meta.RECORD_NUM
      const RECORD_ID = field.allowReservedNameEditing ? value.reservedNameId : 0
      const recordTypeIdSrcField = store.getState().wizard.wizard.fields[props.filingTypeId]
      const recordSubTypeIdSrcField = store.getState().wizard.wizard.fields[props.filingSubTypeId]
      const paramFields = value.parameterFields.map(
        (subField) => store.getState().wizard.wizard.fields[subField]
      )
      let metadata = store.getState().wizard.wizard.meta
      const {
        RECORD_TYPE_ID: defaultRecordTypeId = 0,
        RECORD_SUBTYPE_ID: defaultRecordSubtypeId = 0,
      } = metadata
      const RECORD_TYPE_ID = recordTypeIdSrcField ? recordTypeIdSrcField.value : defaultRecordTypeId
      const RECORD_SUBTYPE_ID =
        recordSubTypeIdSrcField && recordSubTypeIdSrcField.value !== ''
          ? recordSubTypeIdSrcField.value
          : defaultRecordSubtypeId
      var payload = {
        // ...metadata,
        ADDITIONAL_PARAMS: Object.fromEntries(
          paramFields.map((pf) => [
            [pf.name],
            typeof pf.value === 'object' ? JSON.stringify(pf.value) : pf.value,
          ])
        ),
        FILING_NAME: value.val1,
        RESERVED_FILING_ID: value.reservedNameId === '' ? null : value.reservedNameId,
        RECORD_NUM: !!RECORD_NUM ? RECORD_NUM : '',
        RECORD_ID: RECORD_ID,
        FIELD_NAME: field.name,
        CORRECTION_YN: store.getState().wizard.correctionMode,
        RECORD_TYPE_ID,
        RECORD_SUBTYPE_ID,
      }
      const validationType =
        (props.validationEndpointOverride || '') === ''
          ? 'NameLookup'
          : `NameLookup/${props.validationEndpointOverride}`
      api.VALIDATE(validationType, payload, (data) => {
        if (data.valid) {
          callback(true, '')
        } else {
          callback(false, data.error)
        }
      })
    }
  },
  nameRequired: (value, field, props = {}, callback) => {
    if (
      (valueIsNotEmptyOrNull(value.FIRST_NAME) || props.onlyLastNameRequired) &&
      valueIsNotEmptyOrNull(value.LAST_NAME)
    ) {
      callback(true, '')
    } else {
      callback(
        false,
        props.errorMessage !== ''
          ? props.errorMessage
          : props.onlyLastNameRequired
          ? 'Last name is required.'
          : 'First and last name are required.'
      )
    }
  },
  nameOrgRequired: (value, field, props = {}, callback) => {
    const isOrganization =
      field.type === 'NameQualifiedOrg' ? value.ORG_TYPE.toString() === '2' : value.ORG_YN
    if (isOrganization) {
      if (valueIsNotEmptyOrNull(value.ORG_NAME)) {
        callback(true, '')
      } else {
        callback(false, 'Organization is required')
      }
    } else {
      VALIDATE['nameRequired'](value, field, props, (isValid, error) => {
        callback(isValid, error)
      })
    }
  },
  nameQualifiedOrgRequired: (value, field, props = {}, callback) => {
    if (value.ORG_TYPE.toString() === '1') {
      if (value.SELECTED) {
        callback(true, '')
      } else {
        callback(false, 'An item from the search must be selected')
      }
    } else {
      VALIDATE['nameOrgRequired'](value, field, props, (isValid, error) => {
        callback(isValid, error)
      })
    }
  },
  searchItemRequired: (value, field, props = {}, callback) => {
    if (field.multiSelect ? value.length : value) {
      callback(true, '')
    } else {
      callback(false, props.errorMessage || 'An item from the search must be selected')
    }
  },
  validFeinNum: (value, field, props = {}, callback) => {
    if (!valueIsNotEmptyOrNull(value)) {
      callback(null, '')
    } else {
      let isValidFein = /^(0[1-9]|[1-9]\d)-\d{7}$/.test(value) && value.length === 10
      if (isValidFein) {
        callback(true, '')
      } else {
        callback(
          false,
          props.errorMessage !== ''
            ? props.errorMessage
            : 'Please provide a properly formatted FEIN number to continue.'
        )
      }
    }
  },
  validPhoneNumber: (value, field, props = {}, callback) => {
    let isValidPhoneNumber = /\([2-9]\d{2}\) [2-9]\d{2}-\d{4}/.test(value) && value.length === 14
    if (isValidPhoneNumber) {
      callback(true, '')
    } else {
      callback(
        false,
        props.errorMessage !== ''
          ? props.errorMessage
          : 'Provide a valid 10-digit phone number to continue.'
      )
    }
  },
  validPhoneNumberIfPresent: (value, field, props = {}, callback) => {
    if (value && typeof value === 'string' && value.length > 0) {
      let isValidPhoneNumber = /\([2-9]\d{2}\) [2-9]\d{2}-\d{4}/.test(value) && value.length === 14
      if (isValidPhoneNumber) {
        callback(true, '')
      } else {
        callback(
          false,
          props.errorMessage !== ''
            ? props.errorMessage
            : 'Provide a valid 10-digit phone number to continue.'
        )
      }
    } else {
      callback(null, '')
    }
  },
  enforceMaxDaysAfterToday: (value, field, props = {}, callback) => {
    if (field.maxAfterToday === '' || value === null || value === '') {
      callback(null, '')
    } else {
      let date = startOfDay(new Date(value))
      let today = startOfDay(new Date())
      var daysAfterToday = differenceInDays(date, today)
      if (daysAfterToday <= props.maxAfterToday) {
        callback(true, '')
      } else {
        callback(
          false,
          props.errorMessage !== ''
            ? props.errorMessage
            : `Selected date is after the 'maximum days after today': ${props.maxAfterToday}`
        )
      }
    }
  },
  enforceMaxDaysBeforeToday: (value, field, props = {}, callback) => {
    if (field.maxBeforeToday == '' || value === null || value === '') {
      callback(null, '')
    } else {
      let date = startOfDay(new Date(value))
      let now = startOfDay(new Date())
      var daysBeforeToday = differenceInDays(now, date)
      if (daysBeforeToday <= props.maxBeforeToday) {
        callback(true, '')
      } else {
        callback(
          false,
          props.errorMessage !== ''
            ? props.errorMessage
            : `Selected date is before the 'maximum days before today': ${props.maxBeforeToday}`
        )
      }
    }
  },
  enforceMaxMonthsAfterToday: (value, field, props = {}, callback) => {
    if (field.maxAfterToday === '' || value === null || value === '') {
      callback(null, '')
    } else {
      let date = startOfDay(new Date(value))
      let today = startOfDay(new Date())
      var monthsAfterToday = differenceInMonths(date, today)
      if (monthsAfterToday <= props.maxAfterToday) {
        callback(true, '')
      } else {
        callback(
          false,
          props.errorMessage !== ''
            ? props.errorMessage
            : `Selected date is after the 'maximum months after today': ${props.maxAfterToday}`
        )
      }
    }
  },
  enforceMaxMonthsBeforeToday: (value, field, props = {}, callback) => {
    if (field.maxBeforeToday == '' || value === null || value === '') {
      callback(null, '')
    } else {
      let date = startOfDay(new Date(value))
      let now = startOfDay(new Date())
      var monthsBeforeToday = differenceInMonths(now, date)
      if (monthsBeforeToday <= props.maxBeforeToday) {
        callback(true, '')
      } else {
        callback(
          false,
          props.errorMessage !== ''
            ? props.errorMessage
            : `Selected date is before the 'maximum months before today': ${props.maxBeforeToday}`
        )
      }
    }
  },
  enforceMinDaysBeforeToday: (value, field, props = {}, callback) => {
    if (field.minBeforeToday == '' || value === null || value === '') {
      callback(null, '')
    } else {
      let date = startOfDay(new Date(value))
      let now = startOfDay(new Date())
      var daysBeforeToday = differenceInDays(now, date)
      if (daysBeforeToday >= props.minBeforeToday) {
        callback(true, '')
      } else {
        callback(
          false,
          props.errorMessage !== ''
            ? props.errorMessage
            : `Selected date is after the 'minimum days before today': ${props.minBeforeToday}`
        )
      }
    }
  },
  enforceMinDaysAfterToday: (value, field, props = {}, callback) => {
    if (field.minAfterToday === '' || value === null || value === '') {
      callback(null, '')
    } else {
      let date = startOfDay(new Date(value))
      let today = startOfDay(new Date())
      var daysAfterToday = differenceInDays(date, today)
      if (daysAfterToday >= props.minAfterToday) {
        callback(true, '')
      } else {
        callback(
          false,
          props.errorMessage !== ''
            ? props.errorMessage
            : `Selected date is before the 'minimum days after today': ${props.minAfterToday}`
        )
      }
    }
  },
  enforceMinMonthsAfterToday: (value, field, props = {}, callback) => {
    if (field.minAfterToday === '' || value === null || value === '') {
      callback(null, '')
    } else {
      let date = startOfDay(new Date(value))
      let today = startOfDay(new Date())
      var monthsAfterToday = differenceInMonths(date, today)
      if (monthsAfterToday >= props.minAfterToday) {
        callback(true, '')
      } else {
        callback(
          false,
          props.errorMessage !== ''
            ? props.errorMessage
            : `Selected date is before the 'minimum months after today': ${props.minAfterToday}`
        )
      }
    }
  },
  enforceMinMonthsBeforeToday: (value, field, props = {}, callback) => {
    if (field.minBeforeToday == '' || value === null || value === '') {
      callback(null, '')
    } else {
      let date = startOfDay(new Date(value))
      let now = startOfDay(new Date())
      var daysBeforeToday = differenceInMonths(now, date)
      if (daysBeforeToday >= props.minBeforeToday) {
        callback(true, '')
      } else {
        callback(
          false,
          props.errorMessage !== ''
            ? props.errorMessage
            : `Selected date is after the 'minimum months before today': ${props.minBeforeToday}`
        )
      }
    }
  },
  enforceSignatureDateRange: (value, field, props = {}, callback, { event, subField }) => {
    const rules = ['enforceMaxDaysBeforeToday', 'enforceMaxDaysAfterToday']

    const rowPromises = value.map(
      (row) =>
        new Promise((resolve) => {
          const rulePromises = rules.map(
            (ruleId) =>
              new Promise((subResolve) => {
                getIsValid(ruleId, row.date, field, props, (isValid, errorMessage) => {
                  subResolve({ ruleId, isValid, errorMessage })
                })
              })
          )
          Promise.all(rulePromises).then((ruleResolves) => {
            const bothValid = !ruleResolves.some((result) => result.isValid === false)
            let errorMessage = !bothValid
              ? ruleResolves.find((result) => result.isValid === false).errorMessage
              : ''
            resolve({ ruleResolves, bothValid, errorMessage })
          })
        })
    )
    Promise.all(rowPromises).then((rowResolves) => {
      const allValid = !rowResolves.some((result) => result.bothValid === false)
      callback(allValid, rowResolves)
    })
  },
  validZipCode: (value, field, props = {}, callback) => {
    let isZipCode = value === '' ? true : false
    isZipCode =
      /(^\d{5}$)|(^\d{5}-\d{4}$)/.test(value) && (value.length === 10 || value.length === 5)
    if (isZipCode) {
      callback(true, '')
    } else {
      callback(
        false,
        props.errorMessage !== '' ? props.errorMessage : 'Correct this field to continue'
      )
    }
  },
  validAddressZipCode: (value, field, props = {}, callback) => {
    let newValue = value.POSTAL_CODE
    let isZipCode =
      /(^\d{5}$)|(^\d{5}-\d{4}$)/.test(newValue) &&
      (newValue.length === 10 || newValue.length === 5)
    if (isZipCode) {
      callback(true, '')
    } else {
      callback(
        false,
        props.errorMessage !== '' ? props.errorMessage : 'Correct this field to continue'
      )
    }
  },
  validSocial: (value, field, props = {}, callback) => {
    if (!valueIsNotEmptyOrNull(value)) {
      callback(null, '')
    } else {
      let isValidSocial = /^(\d{3}-(?!00)\d{2}-(?!0{4})\d{4})$/.test(value) && value.length === 11
      if (isValidSocial) {
        callback(true, '')
      } else {
        callback(
          false,
          props.errorMessage !== ''
            ? props.errorMessage
            : 'Please provide a properly formatted Social Security Number to continue'
        )
      }
    }
  },
  enforceUploadPlaceholderRules: (value, field, props = {}, callback) => {
    const valueMethod =
      store.getState().wizard.requirePrintMailMethod.size > 0 ? 'mail' : value.method
    const method = field.type === 'UploadAndSubmit' && valueMethod ? valueMethod : null
    const secondaryValidation = store.getState().wizard.secondaryValidation
    if (method === 'mail') {
      callback(true, '')
    } else {
      const uploadPlaceholders = getUploadsFromFields(store.getState().wizard.wizard.fields)
      const computedValue = (field.type === 'UploadAndSubmit' ? value.files : value) || {}
      let rulePromises = []

      uploadPlaceholders.map((placeholder) => {
        if (
          secondaryValidation &&
          !placeholder.requireDoubleValidation &&
          placeholder.parent.type !== 'REPEATER'
        ) {
          console.warn(
            `double validation: disabled field business rules not run. fieldId: ${placeholder.fieldId}`
          )
          return null
        }
        rulePromises.push(
          new Promise((resolve) => {
            loopThroughRules(
              {
                businessRules: placeholder.businessRules,
                value: computedValue[placeholder.fieldId],
                field: placeholder,
                event: 'deferred',
              },
              (validation) => {
                resolve({ ...validation, fieldId: placeholder.fieldId })
              }
            )
          })
        )
        return null
      })
      Promise.all(rulePromises).then((values) => {
        const allValid = values.reduce((isValid, currentVal) => {
          if (currentVal.valids.includes(false)) {
            return false
          } else {
            return isValid
          }
        }, true)
        const validations = values.reduce((valueObject, currentVal) => {
          valueObject[currentVal.fieldId] = {
            ...currentVal,
            isValid: !currentVal.valids.includes(false),
          }
          return valueObject
        }, {})
        callback(allValid, validations)
      })
    }
  },
  uploadRequired: (value, field, props = {}, callback) => {
    if (!Array.isArray(value)) {
      callback(false, 'Upload is required')
    } else {
      const atLeastOneUploadSucceeded = value.find((file) => file.status === 'success')
      if (value.length > 0 && atLeastOneUploadSucceeded) {
        callback(true, '')
      } else {
        callback(false, 'Upload is required')
      }
    }
  },
  uploadImmediatelyRequired: (value, field, props = {}, callback) => {
    if (!Array.isArray(value)) {
      callback(false, 'Upload is required')
    } else {
      const atLeastOneUploadSucceeded = value.find((file) => file.status === 'success')
      if (value.length > 0 && atLeastOneUploadSucceeded) {
        callback(true, '')
      } else {
        callback(false, 'Upload is required')
      }
    }
  },
  allSignaturesRequired: (value, field, props = {}, callback) => {
    const fields = store.getState().wizard.wizard.fields
    const printMailRequired = store.getState().wizard.requirePrintMailMethod.size > 0
    const srcRepeater = fields[field.srcRepeater] ? fields[field.srcRepeater] : null
    if (!printMailRequired) {
      if (field.multiple) {
        if (srcRepeater) {
          if (srcRepeater.value.length === value.length) {
            const allSignaturesFilled =
              value.length > 0
                ? value.every(
                    (signature) =>
                      valueIsNotEmptyOrNull(signature.sig) &&
                      valueIsNotEmptyOrNull(signature.date) &&
                      signature.date.length >= 10 &&
                      (field.showCapacity ? valueIsNotEmptyOrNull(signature.capacity) : true)
                  )
                : true
            if (allSignaturesFilled) {
              callback(true, '')
            } else {
              callback(false, 'All signatures are required.')
            }
          } else {
            callback(false, 'All signatures are required.')
          }
        } else {
          callback(false, '')
        }
      } else {
        if (value.length === 0) {
          callback(false, 'All signature fields are required.')
        } else {
          if (
            valueIsNotEmptyOrNull(value[0].sig) &&
            valueIsNotEmptyOrNull(value[0].date) &&
            value[0].date.length >= 10 &&
            (field.showCapacity
              ? valueIsNotEmptyOrNull(value[0].capacity) &&
                (value[0].capacity.toLowerCase() !== 'self'
                  ? valueIsNotEmptyOrNull(value[0].behalf)
                  : true)
              : true)
          ) {
            callback(true, '')
          } else {
            callback(false, 'All signature fields are required.')
          }
        }
      }
    } else {
      callback(null, '')
    }
  },
  minRequiredSignatures: (value, field, props = {}, callback) => {
    const fields = store.getState().wizard.wizard.fields
    const srcRepeater = fields[field.srcRepeater] ? fields[field.srcRepeater] : null
    var minRequiredSignatures = props.minRequiredSignatures
    var signatureCount = 0
    var signatureFieldsComplete = true
    if (field.multiple) {
      if (srcRepeater) {
        if (srcRepeater.value.length === value.length && value.length > 0) {
          value.forEach((signature) => {
            if (
              valueIsNotEmptyOrNull(signature.sig) &&
              valueIsNotEmptyOrNull(signature.date) &&
              signature.date.length >= 10 &&
              (field.showCapacity ? valueIsNotEmptyOrNull(signature.capacity) : true)
            ) {
              signatureCount++
              signatureFieldsComplete = true
            } else {
              signatureFieldsComplete = false
            }
          })

          if (signatureCount >= minRequiredSignatures) {
            callback(true, '')
          } else if (!signatureFieldsComplete) {
            callback(false, 'All signature fields and valid date(s) are required.')
          } else {
            callback(
              false,
              `At least ${minRequiredSignatures} signature${
                minRequiredSignatures > 1 ? 's' : ''
              } required.`
            )
          }
        } else {
          callback(
            false,
            `At least ${minRequiredSignatures} signature${
              minRequiredSignatures > 1 ? 's' : ''
            } required`
          )
        }
      } else {
        callback(false, 'This repeater has been configured incorrectly')
      }
    } else {
      if (value.length === 0) {
        callback(false, 'All signature fields are required.')
      } else {
        if (
          valueIsNotEmptyOrNull(value[0].sig) &&
          valueIsNotEmptyOrNull(value[0].date) &&
          value[0].date.length === 10 &&
          (field.showCapacity
            ? valueIsNotEmptyOrNull(value[0].capacity) //&&
            : // (value[0].capacity !== 'self' ? valueIsNotEmptyOrNull(value[0].behalf) : true)
              true)
        ) {
          callback(true, '')
        } else {
          callback(false, 'All signature fields and valid date(s) are required.')
        }
      }
    }
  },
  formationLocaleRequired: (value, field, props = {}, callback) => {
    if (valueIsNotEmptyOrNull(value.FormationTypeId)) {
      if (valueIsNotEmptyOrNull(value[value.FormationTypeId])) {
        callback(true, '')
      } else {
        callback(false, props.errorMessage || 'Formation locale is required')
      }
    } else {
      callback(false, props.errorMessage || 'Formation locale is required')
    }
  },
  validateEntityNameFields: (value, field, props = {}, callback, { event, subField }) => {
    const onFirstBlurRules = ['entityNameNotTaken', 'entityIsValidName', 'entityNameRequired']
    const onSecondBlurRules = [
      'entityNameNotTaken',
      'entityValuesMatch',
      'entityNameRequired',
      'entityIsValidName',
    ]
    const onBlurRules = [...onFirstBlurRules, 'entityValuesMatch']
    const rules = [
      ...Object.keys(props).filter(
        (ruleId) => ruleId !== 'filingTypeId' && ruleId !== 'filingSubTypeId'
      ),
      ...onBlurRules,
      'mustSelectReservedOption',
      'mustSelectAdditionalOption',
      'mustSelectConsentOption',
      'consentUploadRequired',
    ]
    let executableRules = rules
    if (event === 'onBlur') {
      if (subField === 'first') executableRules = onFirstBlurRules
      if (subField === 'second') executableRules = onSecondBlurRules
    }
    let removableRules = []
    if (value.additionalYN === 'Y') {
      removableRules.push('entityIsValidName')
      removableRules.push('entityNameNotTaken')
    }
    if (props.disableNameNotTakenCheck) {
      removableRules.push('entityNameNotTaken')
    }
    if (value.uploadField.length > 0) {
      if (!removableRules.includes('entityNameNotTaken')) {
        removableRules.push('entityNameNotTaken')
      }
      store.dispatch({
        type: 'ADD_INTERNAL_REVIEW_REQUIRED',
        fieldId: field.fieldId,
      })
    } else {
      if (!removableRules.includes('entityNameNotTaken')) {
        removableRules = removableRules.filter((ruleId) => ruleId !== 'entityNameNotTaken')
      }
      store.dispatch({
        type: 'REMOVE_INTERNAL_REVIEW_REQUIRED',
        fieldId: field.fieldId,
      })
    }
    executableRules = executableRules.filter((ruleId) => !removableRules.includes(ruleId))
    const rulePromises = executableRules.map(
      (ruleId) =>
        new Promise((resolve) => {
          getIsValid(
            ruleId,
            value,
            field,
            {
              filingTypeId: props.filingTypeId,
              filingSubTypeId: props.filingSubTypeId,
              validationEndpointOverride: props.validationEndpointOverride || '',
              useConversionXref: props.useConversionXref || false,
            },
            (isValid, errorMessage, payload) => {
              resolve({ ruleId, isValid, errorMessage, payload })
            }
          )
        })
    )
    Promise.all(rulePromises).then((values) => {
      const allValid = !values.some((result) => result.isValid === false)
      let errors = values.reduce((obj, current) => {
        return {
          ...obj,
          [current.ruleId]: { isValid: current.isValid, errorMessage: [current.errorMessage] },
        }
      }, {})
      let payloads = values.reduce((obj, current = {}) => {
        return { ...obj, ...current.payload }
      }, {})
      if (event === 'onBlur' && subField === 'first' && allValid) {
        payloads.nameMatch = null
      }
      payloads.setValue = false
      callback(allValid, errors, payloads)
    })
  },
  validateIndividualName: (value, field, props, callback) => {
    const isFullName = value.FIRST_NAME && value.LAST_NAME
    if ((value.ORG_YN && value.ORG_YN === 'N') || !isFullName) callback(true, '')
    else {
      api.VALIDATE_INDIVIDUAL_NAME(
        props.validationEndpointOverride || 'DuplicateNotaryName',
        {
          FirstName: value.FIRST_NAME,
          MiddleName: value.MIDDLE_NAME,
          LastName: value.LAST_NAME,
          Suffix: value.SUFFIX,
        },
        (res) => {
          if (!res.NoQueryMade) {
            if (props.hardStopWhenValidationFails) {
              if (res.IsValid) {
                callback(true, '', { infoMessage: props.informationMessage })
              } else {
                callback(false, props.errorMessage, { infoMessage: null })
              }
            } else {
              callback(true, '', {
                infoMessage: res.IsValid ? props.informationMessage : props.errorMessage,
              })
            }
          } else callback()
        },
        (err) => {
          callback(null, 'Individual Name Validation endpoint failed.')
        }
      )
    }
  },
  validateOrganizationName: (value, field, props, callback) => {
    // ORG_TYPE '2' on NameQualifiedOrg = unqualified organization
    const isOrganization = value.ORG_TYPE.toString() === '2'
    const hasOrgName = isOrganization && value.ORG_NAME
    if (!hasOrgName || !props.validationEndpointOverride) callback(true, '')
    else {
      api.VALIDATE_INDIVIDUAL_NAME(
        props.validationEndpointOverride,
        {
          IsOrganization: true,
          OrganizationName: value.ORG_NAME,
        },
        (res) => {
          if (!res.NoQueryMade) {
            if (props.hardStopWhenValidationFails) {
              if (res.IsValid) {
                callback(true, '', { infoMessage: props.informationMessage })
              } else {
                callback(false, props.errorMessage, { infoMessage: null })
              }
            } else {
              callback(true, '', {
                infoMessage: res.IsValid ? props.informationMessage : props.errorMessage,
              })
            }
          } else callback()
        },
        (err) => {
          callback(null, 'Organization Name Validation endpoint failed.')
        }
      )
    }
  },
  mustSelectReservedOption: (value, field, props = {}, callback) => {
    if (value.reservedYN === 'Y' || value.reservedYN === 'N' || field.hideReserved) {
      callback(true, '')
    } else {
      callback(false, 'This field is required.')
    }
  },
  mustSelectReservedName: (value, field, props = {}, callback) => {
    if (value.reservedYN === 'Y') {
      if (valueIsNotEmptyOrNull(value.reservedNameId)) {
        callback(true, '')
      } else {
        callback(false, 'Please select a reserved name with which you wish to file.')
      }
    } else {
      callback(null, '')
    }
  },
  entityNameRequired: (value, field, props = {}, callback) => {
    if (
      value.reservedYN === 'N' ||
      field.hideReserved ||
      (field.allowReservedNameEditing && value.reservedYN === 'Y')
    ) {
      if (valueIsNotEmptyOrNull(value.val1)) {
        callback(true, '')
      } else {
        callback(false, 'Please provide the entity name to continue.')
      }
    } else {
      callback(null, '')
    }
  },
  mustSelectAdditionalOption: (value, field, props = {}, callback) => {
    if ((value.reservedYN === 'N' || field.hideReserved) && !field.hideAdditionalName) {
      if (value.additionalYN === 'Y' || value.additionalYN === 'N') {
        callback(true, '')
      } else {
        callback(false, 'This field is required.')
      }
    } else {
      callback(null, '')
    }
  },
  mustSelectAdditionalName: (value, field, props = {}, callback) => {
    if ((value.reservedYN === 'N' || field.hideReserved) && value.additionalYN == 'Y') {
      if (valueIsNotEmptyOrNull(value.additionalNameId)) {
        callback(true, '')
      } else {
        callback(false, 'Please select a name to continue.')
      }
    } else {
      callback(null, '')
    }
  },
  mustSelectConsentOption: (value, field, props = {}, callback) => {
    if (
      !field.hideConsent &&
      (value.reservedYN === 'N' || field.hideReserved) &&
      (value.additionalYN === 'N' || field.hideAdditionalName)
    ) {
      if (value.consentYN === 'Y' || value.consentYN === 'N') {
        callback(true, '')
      } else {
        callback(false, 'This field is required.')
      }
    } else {
      callback(null, '')
    }
  },
  consentUploadRequired: (value, field, props = {}, callback) => {
    if (value.consentYN !== 'Y') {
      callback(true, '')
    } else {
      if (!Array.isArray(value.uploadField)) {
        callback(false, 'Consent upload is required')
      } else {
        const atLeastOneUploadSucceeded = value.uploadField.find(
          (file) => file.status === 'success'
        )
        if (value.uploadField.length > 0 && atLeastOneUploadSucceeded) {
          callback(true, '')
        } else {
          callback(false, 'Consent upload is required')
        }
      }
    }
  },
  numConsentsRequired: (value, field, props = {}, callback) => {
    if (
      (value.reservedYN === 'N' || field.hideReserved) &&
      (value.additionalYN === 'N' || field.hideAdditionalName) &&
      value.consentYN === 'Y' &&
      !field.hideNumConsent
    ) {
      if (valueIsNotEmptyOrNull(value.numConsents) && valueIsANumber(value.numConsents)) {
        if (value.numConsents <= 100) {
          callback(true, '')
        } else {
          callback(false, 'The number of consents cannot exceed 100.')
        }
      } else {
        callback(false, 'Please provide the number of consents to continue.')
      }
    } else {
      callback(null, '')
    }
  },
  entityValuesMatch: (value, field, props = {}, callback) => {
    if (value.reservedYN === 'N' || field.hideReserved) {
      VALIDATE.valuesMatch(value, field, props, (isValid, errorMessage) => {
        callback(isValid, errorMessage, { nameMatch: isValid })
      })
    } else {
      callback(null, '')
    }
  },
  entityNameNotTaken: (value, field, props = {}, callback) => {
    if (
      (value.reservedYN === 'N' || field.hideReserved || field.allowReservedNameEditing) &&
      value.consentYN !== 'Y'
    ) {
      VALIDATE.nameNotTaken(value, field, props, (isValid, errorMessage) => {
        callback(isValid, errorMessage)
      })
    } else {
      callback(null, '')
    }
  },
  enitityHasNameEnding: (value, field, props = {}, callback) => {
    if (value.reservedYN === 'N' || field.hideReserved) {
      VALIDATE.hasNameEnding(value, field, props, (isValid, errorMessage) => {
        callback(isValid, errorMessage)
      })
    } else {
      callback(null, '')
    }
  },
  entityNoRegulatedWords: (value, field, props = {}, callback) => {
    if (value.reservedYN === 'N' || field.hideReserved) {
      VALIDATE.noRegulatedWords(value, field, props, (isValid, errorMessage) => {
        callback(isValid, errorMessage)
      })
    } else {
      callback(null, '')
    }
  },
  entityIsValidName: (value, field, props = {}, callback) => {
    VALIDATE.isValidName(value, field, props, (isValid, errorMessage, payload) => {
      callback(isValid, errorMessage, payload)
    })
  },
  mailingPoBoxNotAllowed: (value, field, props = {}, callback) => {
    VALIDATE.poBoxNotAllowed(
      value.MAIL_ADDR,
      field,
      props,
      (valid, errorMessage, validationProps) => {
        if (validationProps.hasOwnProperty('validAddress')) {
          let validAddress = validationProps.validAddress
          callback(valid, errorMessage, { validMailAddress: validAddress })
        } else {
          callback(valid, errorMessage)
        }
      }
    )
  },
  selectedAgentOrganization: (value, field, props = {}, callback) => {
    if (value.isIndividual) {
      callback(true, '', { validAgentOrg: true })
    } else if (
      value.ORGANIZATION &&
      value.ORGANIZATION !== null &&
      value.ORGANIZATION.SOURCE_ID !== 0
    ) {
      callback(true, '', { validAgentOrg: true })
    } else {
      callback(false, 'Please select an Organization to continue.', { validAgentOrg: false })
    }
  },
  poBoxNotAllowed: (value, field, props = {}, callback) => {
    const poBoxRegex = /\b(?:p\.?\s*o\.?|post\s+office)(\s+)?(?:box|b|[0-9]*)?\b.*/i
    const pmbRegex = /\b(?:p(?:ersonal)?\.?\s*[m](?:ail)?\s*b(?:[o]x)?).*/i //Personal Mail Box pattern
    if (value.ORG_YN) {
      callback(true, '', { validAddress: true })
    } else if (emptyExceptDefaultState(getAddressObject(value), field)) {
      callback(null, '', { validAddress: null })
    } else if (
      poBoxRegex.test(value.ADDR1) ||
      poBoxRegex.test(value.ADDR2) ||
      poBoxRegex.test(value.ADDR3) ||
      pmbRegex.test(value.ADDR1) ||
      pmbRegex.test(value.ADDR2) ||
      pmbRegex.test(value.ADDR3) ||
      value.ADDR1.toLowerCase().startsWith('box ') ||
      (value.ADDR2 && value.ADDR2.toLowerCase().startsWith('box ')) ||
      (value.ADDR3 && value.ADDR3.toLowerCase().startsWith('box '))
    ) {
      callback(false, props.errorMessage || 'Address may not contain a PO Box', {
        validAddress: false,
      })
    } else {
      callback(true, '', { validAddress: true })
    }
  },
  dateRangeRequired: (value, field, props = {}, callback) => {
    if (valueIsNotEmptyOrNull(value.start) && valueIsNotEmptyOrNull(value.end)) {
      callback(true, '')
    } else {
      callback(false, props.errorMessage || 'A date range is required.')
    }
  },
  minDate: (value, field, props = {}, callback) => {
    if (props.minDateOptions !== 'custom') {
      api.VALIDATE(
        'MinServerDate',
        {
          value: value,
          source: props.minDateOptions,
          metadata: store.getState().wizard.wizard.meta,
        },
        (valid) => {
          callback(valid.valid, valid.error)
        }
      )
    } else {
      if (isAfter(new Date(value), new Date(props.customMinDate))) {
        callback(true, '')
      } else {
        callback(false, props.errorMessage || `Please select a date after ${props.customMinDate}`)
      }
    }
  },
  maxDate: (value, field, props = {}, callback) => {
    if (props.maxDateOptions !== 'custom') {
      api.VALIDATE(
        'MaxServerDate',
        {
          value: value,
          source: props.maxDateOptions,
          metadata: store.getState().wizard.wizard.meta,
        },
        (valid) => {
          callback(valid.valid, valid.error)
        }
      )
    } else {
      if (isBefore(new Date(value), new Date(props.customMaxDate))) {
        callback(true, '')
      } else {
        callback(false, props.errorMessage || `Please select a date before ${props.customMaxDate}`)
      }
    }
  },
  minDateRange: (value, field, props = {}, callback) => {
    if (props.minDateOptions !== 'custom') {
      api.VALIDATE(
        'MinServerDate',
        {
          value: value.start,
          source: props.minDateOptions,
          metadata: store.getState().wizard.wizard.meta,
        },
        (valid) => {
          callback(valid.valid, valid.error)
        }
      )
    } else if (isAfter(new Date(value.start), new Date(props.customMinDate))) {
      callback(true, '')
    } else {
      callback(false, props.errorMessage || `Please select a date after ${props.customMinDate}`)
    }
  },
  maxDateRange: (value, field, props = {}, callback) => {
    if (props.minDateOptions !== 'custom') {
      api.VALIDATE(
        'MaxServerDate',
        {
          value: value.end,
          source: props.minDateOptions,
          metadata: store.getState().wizard.wizard.meta,
        },
        (valid) => {
          callback(valid.valid, valid.error)
        }
      )
    } else if (isBefore(new Date(value.end), new Date(props.customMaxDate))) {
      callback(true, '')
    } else {
      callback(false, props.errorMessage || `Please select a date before ${props.customMaxDate}`)
    }
  },
  repeaterColumnsMustBeUnique: (value, field, props = {}, callback) => {
    const constraints = props.constraints
    const rows = field.value.filter((row) => row._statusId !== 3)
    const fields = store.getState().wizard.wizard.fields
    const results = testRepeaterConstraints(constraints, rows, fields)
    const errors = results.reduce((errs, result, i) => {
      if (result === false) {
        const constraint = constraints[i]
        const constFields = constraint.fields
          .map((fieldId) =>
            fields[fieldId] ? fields[fieldId].shortLabel || fields[fieldId].label : null
          )
          .filter((x) => x)
        return [
          ...errs,
          constraint.errorMessage ||
            `At least one of the following fields must be unique: ${constFields.join(', ')}. \n`,
        ]
      }
      return errs
    }, [])
    const isValid = results.every((t) => t === true)
    callback(isValid, errors)
  },
  isValidEmailFormat: (value, field, props = {}, callback) => {
    // const regex = /^(([^<>()[\].,;:\s@"]+(\.[^<>()[\].,;:\s@"]+)*)|(".+"))@(([^<>()[\].,;:\s@"]+\.)+[^<>()[\].,;:\s@"]{2,})$/i;
    const regex = /(.{1,}?)@(.{1,}?)\.(.{1,})/g
    const sanitizedValue = ((field.type === 'confirmFields' ? value.val1 : value) || '').trim()
    if (valueIsNotEmptyOrNull(sanitizedValue)) {
      const isValid = regex.test(sanitizedValue) && !sanitizedValue.includes(' ')
      if (isValid) {
        callback(true, '')
      } else {
        callback(false, props.errorMessage || 'Please enter a properly formatted email address.')
      }
    } else {
      callback(null, '')
    }
  },
  timeRequired: (value, field, props = {}, callback) => {
    if (value.length >= 7) {
      callback(true, '')
    } else {
      callback(false, props.errorMessage || 'This field is required.')
    }
  },
  uccSearchRequired: (value, field, props = {}, callback) => {
    const UCC4Satisfied =
      field.uccMode.toString() === '4'
        ? value.search.SEARCH_UCC ||
          value.search.SEARCH_GOV ||
          value.search.SEARCH_EFS ||
          value.search.SEARCH_RA_LIEN ||
          value.search.SEARCH_AG_LIEN
        : true
    const commonFieldCheck =
      (value.search.IS_ORG === true || value.search.IS_ORG === 'true'
        ? value.search.SEARCH_ORG_NAME
        : process.env.UCC_SEARCH_ONLY_REQUIRE_LAST_NAME
        ? value.search.SEARCH_LAST_NAME
        : value.search.SEARCH_FIRST_NAME && value.search.SEARCH_LAST_NAME) && UCC4Satisfied
    const checkCommonFields = (value) => {
      if (value.cache && commonFieldCheck) {
        callback(true, '')
      } else {
        let errorMessage = []
        if (!value.cache) {
          errorMessage.push('You must execute a search.')
        }
        if (value.search.IS_ORG === true || value.search.IS_ORG === 'true') {
          if (!value.search.SEARCH_ORG_NAME) {
            errorMessage.push('Organization name is required.')
          }
        } else {
          if (
            process.env.UCC_SEARCH_ONLY_REQUIRE_LAST_NAME
              ? !value.search.SEARCH_LAST_NAME
              : !value.search.SEARCH_FIRST_NAME || !value.search.SEARCH_LAST_NAME
          ) {
            errorMessage.push(
              process.env.UCC_SEARCH_ONLY_REQUIRE_LAST_NAME
                ? 'A last name must be entered'
                : 'First and last name are required.'
            )
          }
        }
        if (!UCC4Satisfied) {
          errorMessage.push('At least one of the UCC 4 search types must be selected.')
        }
        callback(false, errorMessage.join('\n'))
      }
    }
    if (process.env.UCC_SEARCH_ALLOW_SEARCH_TYPE) {
      if (
        value.cache &&
        (value.search.SEARCH_TYPE === 'UCC_NUM' ? value.SEARCH_UCC_NUM !== '' : commonFieldCheck)
      ) {
        callback(true, '')
      } else {
        let errorMessage = []
        if (value.search.SEARCH_TYPE === 'UCC_NUM') {
          if (!value.cache) {
            errorMessage.push('You must execute a search.')
          }
          if (!value.search.SEARCH_UCC_NUM) {
            errorMessage.push('File number is required.')
          }
          callback(false, errorMessage.join('\n'))
        } else {
          checkCommonFields(value)
        }
      }
    } else {
      checkCommonFields(value)
    }
  },
  uccSearchResultHasPages: (value, field, props = {}, callback) => {
    // PAGE_COUNT, 'Page Count'
    const defaultErrorMessage = 'All results must have at least 1 page.'
    if (!value.cache) callback(false, props.errorMessage || defaultErrorMessage)
    const { rawRows = {} } = value.cache
    const resultWithNoPages = (value.ids || []).find((id) => {
      if (rawRows[id] && rawRows[id]['PAGE_COUNT'] === '0') return true
      return false
    })
    const validatePages = () => {
      if (resultWithNoPages) {
        callback(false, props.errorMessage || defaultErrorMessage)
      } else {
        callback(true, '')
      }
    }
    if (props.doesNotApplyToSearchReport) {
      if (value.search.SEARCH_REQUEST_TYPE === 'LIST_ONLY') {
        callback(null, '')
      } else {
        validatePages()
      }
    } else {
      validatePages()
    }
  },
  atLeastOneUccSearchResultIsRequired: (value, field, props = {}, callback) => {
    const isPartial = value.IS_PARTIAL
    const lengthIsSatisfied = true //value.cache && value.cache.rows.length > 0
    const defaultErrorMessage = 'Search must return at least one result.'
    const checkLength = () => {
      if (props.onlyRequireWhenExists) {
        if (!lengthIsSatisfied && isPartial) {
          callback(false, props.errorMessage || defaultErrorMessage)
        } else {
          callback(true, '')
        }
      } else {
        if (lengthIsSatisfied) {
          callback(true, '')
        } else {
          callback(false, props.errorMessage || defaultErrorMessage)
        }
      }
    }
    if (props.onlyAppliesToFileNumber) {
      if (value.search.SEARCH_TYPE !== 'UCC_NUM') {
        callback(null, '')
      } else {
        checkLength()
      }
    } else {
      checkLength()
    }
  },
  mustSelectLocalSelect: (value, field, props = {}, callback) => {
    const fields = store.getState().wizard.wizard.fields
    const srcRepeater = fields[field.srcRepeater]
    const srcField = fields[field.srcField]
    if (srcRepeater && srcField) {
      if (valueIsNotEmptyOrNull(value)) {
        const valueExists = srcRepeater.value.find((row) => row._rowId === value)
        if (valueExists) {
          callback(true, '')
        } else {
          callback(false, props.errorMessage || 'Select an option to continue.')
        }
      } else {
        callback(false, props.errorMessage || 'Select an option to continue.')
      }
    } else {
      callback(false, 'You must assign a Source Repeater and a Source Field to use this control.')
    }
  },
  enforceRepeaterChildConstraints: (value, field, props = {}, callback) => {
    if (value.length > 0) {
      const fields = store.getState().wizard.wizard.fields
      const columns = field.template.map((fieldId) => fields[fieldId])
      validateRepeaterConstraints(columns, field).then((resolutions) => {
        const isValids = resolutions.map((r) => r.isValid)
        const errors = resolutions.map((r) => r.errorMessage).join('\n')
        if (isValids.some((v) => v === false)) {
          callback(false, errors)
        } else if (isValids.some((v) => v === true)) {
          callback(true, '')
        } else {
          callback(null, '')
        }
      })
    } else {
      callback(null)
    }
  },
  minMultiComplete: (value, field, props = {}, callback) => {
    if (value.length >= props.min) {
      callback(true, '')
    } else {
      callback(false, props.errorMessage || `You must select at least ${props.min} to continue.`)
    }
  },
  efsRequired: (value, field, props = {}, callback) => {
    if (value.yearType) {
      switch (value.yearType) {
        case 'YearDuration':
          if (value.startYear && value.endYear) callback(true, '')
          else
            callback(
              false,
              props.durationErrorMessage || 'Select a start and end year to continue.'
            )
          break
        case 'Arbitrary':
          if (value.years.length > 0) callback(true, '')
          else
            callback(false, props.arbitraryErrorMessage || 'Select at least one year to continue.')
          break
        case 'AllYears':
        case 'LienDuration':
        default:
          callback(true, '')
      }
    } else {
      callback(false, props.errorMessage || 'Select a crop year to continue.')
    }
  },
  mayOnlyAddAdditionalRows: (value, field, props = {}, callback) => {
    const original = store.getState().wizard.diff.originalByFieldId[field.fieldId]
    const prevNumRows = original ? original.length : 0
    const numRows = value.length
    if (numRows <= prevNumRows + props.numRows) {
      callback(true, '')
    } else {
      callback(
        false,
        props.errorMessage || `You may not add more than ${prevNumRows + props.numRows} row(s).`
      )
    }
  },
  mustHaveSameNumRowsAs: (value, field, props = {}, callback) => {
    const { repeater } = props
    if (repeater) {
      const otherRepeater = store.getState().wizard.wizard.fields[repeater]
      if (otherRepeater) {
        if (otherRepeater.value.length === field.value.length) {
          callback(true, '')
        } else {
          callback(
            false,
            props.errorMessage || `You must provide exactly ${otherRepeater.value.length} row(s).`
          )
        }
      } else {
        callback(null, '')
      }
    } else {
      callback(false, 'This business rule failed because a repeater was not selected.')
    }
  },
  onlyNumbers: (value, field, props = {}, callback) => {
    if (!valueIsNotEmptyOrNull(value)) {
      callback(null, '')
    } else if (
      props.allowCommas
        ? !/^((\d+|\d{1,3}(,\d{3})*)(\.\d+)?|\.\d+)$/.test(value)
        : /\D+/g.test(value)
    ) {
      callback(false, props.errorMessage || 'This field must only contain numbers.')
    } else {
      callback(true, '')
    }
  },
  maxCharacters: (value, field, props = {}, callback) => {
    if (valueIsNotEmptyOrNull(value)) {
      if (value.length > props.maxCharacters) {
        callback(
          false,
          props.errorMessage ||
            `This field may not contain more than ${props.maxCharacters} character(s).`
        )
      } else {
        callback(true)
      }
    } else {
      callback(null)
    }
  },
  minCharacters: (value, field, props = {}, callback) => {
    if (valueIsNotEmptyOrNull(value)) {
      if (value.length < props.minCharacters) {
        callback(
          false,
          props.errorMessage ||
            `This field must contain at least ${props.minCharacters} character(s).`
        )
      } else {
        callback(true)
      }
    } else {
      callback(null)
    }
  },
  minRepeaterEdit: (value, field, props = {}, callback) => {
    const edited = value.filter((r) => r._statusId === 2)
    if (edited.length < props.minRows) {
      callback(false, `You must edit at least ${props.minRows} row(s) to continue.`)
    } else {
      callback(true, '')
    }
  },
  minRepeaterDelete: (value, field, props = {}, callback) => {
    const deleted = value.filter((r) => r._statusId === 3)
    if ((props.disableIfOne ? value.length >= 1 : true) && deleted.length < props.minRows) {
      callback(
        false,
        props.errorMessage || `You must delete at least ${props.minRows} row(s) to continue.`
      )
    } else {
      callback(true, '')
    }
  },
  checkForRegulatedWords: (value, field, props = {}, callback) => {
    const promises = props.regulatedWordLists.map(
      (category) =>
        new Promise((resolve, reject) => {
          api.VALIDATE(
            'TriggerWords',
            {
              CATEGORY: category,
              VALUE: value,
              FIELD_TYPE: field.type,
            },
            (response) => resolve(response),
            (err) => reject(err)
          )
        })
    )
    Promise.all(promises).then((responses) => {
      const isValid = responses.every((res) => res.valid !== false)
      const errorMessage = responses
        .filter((res) => res.error)
        .map((res) => res.error)
        .join('\n')
      const infoMessage = responses
        .filter((res) => res.payload.INFO_MESSAGE)
        .map((res) => res.payload.INFO_MESSAGE)
        .join('\n')
      const triggerInternalReview = responses.some((res) => res.payload.TRIGGER_INTERNAL_REVIEW)
      if (triggerInternalReview) {
        store.dispatch({
          type: 'ADD_INTERNAL_REVIEW_REQUIRED',
          fieldId: field.fieldId,
        })
      } else {
        store.dispatch({
          type: 'REMOVE_INTERNAL_REVIEW_REQUIRED',
          fieldId: field.fieldId,
        })
      }
      callback(isValid, errorMessage, { infoMessage })
    })
  },
  requireInternalReviewIfResultsContains: (value, field, props = {}, callback) => {
    const isValid =
      value.length !== 0 &&
      value.every((searchresult) =>
        props.webSearchColumns.every(
          (columnToSearch) =>
            searchresult.hasOwnProperty(columnToSearch) &&
            !searchresult[columnToSearch].includes(props.textToValidate)
        )
      )
    //Set internalReviewRequired if the value list contains missing images
    if (!isValid) {
      store.dispatch({
        type: 'ADD_INTERNAL_REVIEW_REQUIRED',
        fieldId: field.fieldId,
      })
    } else {
      store.dispatch({
        type: 'REMOVE_INTERNAL_REVIEW_REQUIRED',
        fieldId: field.fieldId,
      })
    }
    callback(null, '', { infoMessage: isValid ? '' : props.errorMessage }) //We never display an error, just info
  },
  customBusinessRules: (value, field, props = {}, callback, options = {}) => {
    if (props.rules.length) {
      const ruleResults = props.rules.map((rule) =>
        resolveFieldEngine.resolveRootNode(rule.logicNodes, { context: options })
      )
      callback(
        ruleResults.every((res) => res.isValid),
        ruleResults
          .filter((res) => res.isValid === false)
          .map((res) => res.errorMessage)
          .join('\n')
      )
    } else {
      callback(null, '')
    }
  },
  doubleValidateOnAdvance: (value, field, props = {}, callback, options = {}) => {
    let selectedRow = store.getState().workview2.selectedRow
    let location = store.getState().router.location
    let wizard = store.getState().wizard.wizard
    if (
      !location.pathname.includes('office') ||
      !field.requireDoubleValidation ||
      !selectedRow ||
      selectedRow.ProcessingStatusId !== ProcessingStatusIds.PendingSecondaryReview
    ) {
      callback(null, '')
    } else {
      let validateValue = undefined
      if (field.type === 'repeater') {
        validateValue = []
        const idToName = {}
        field.template.forEach((fieldId) => {
          idToName[fieldId] = wizard.fields[fieldId].name
        })
        value.forEach((row) => {
          const updatedRow = {}
          for (let [fieldId, name] of Object.entries(idToName)) {
            updatedRow[name] = row[fieldId]
          }
          if (row._statusId !== undefined) {
            updatedRow._statusId = row._statusId
          }
          validateValue.push(updatedRow)
        })
      }
      const payload = {
        SourceTypeId: selectedRow.SourceTypeId,
        FilingNum: wizard.meta.FilingNum,
        Field: { ...field, validateValue: validateValue },
      }
      // call endpoint and receive validation
      api.VALIDATE_SECONDARY_REVIEW_FIELD(
        payload,
        (res) => {
          callback(
            res.Success,
            res.Message || '',
            res.FieldError || { RowErrors: null, ValueVerifierEntered: null }
          )
        },
        (err) => {
          console.error(err)
          callback(false, `Failed to secondary validate field: ${field.fieldId}`)
        }
      )
    }
  },
}

const getIsValid = (fn, value, field, props, callback, options) => {
  const validateFunction = VALIDATE[fn]
  if (validateFunction) {
    VALIDATE[fn](
      value,
      field,
      props,
      (valid, errorMessage, payload) => {
        callback(valid, errorMessage, payload)
      },
      options
    )
  } else {
    callback(null, '')
  }
}

export const stringifyIndividualName = (value) => {
  let name = `${value.FIRST_NAME} `
  if (value.MIDDLE_NAME) {
    name += `${value.MIDDLE_NAME} `
  }
  name += `${value.LAST_NAME}`
  if (value.SUFFIX) {
    name += ` ${value.SUFFIX}`
  }
  return name.trim()
}

export const loopThroughRules = ({ businessRules, value, field, event, options }, callback) => {
  let validation = {
    valids: [],
    errors: [],
    payload: {},
  }
  var promises = []
  for (var i = 0; i < businessRules.length; i++) {
    var rule = businessRules[i]
    promises[i] = new Promise((resolve, reject) => {
      if (BUSINESS_RULES[rule.fn] && BUSINESS_RULES[rule.fn][event] === true) {
        getIsValid(
          rule.fn,
          value,
          field,
          rule.props,
          (valid, errorMessage, payload) => {
            if (valid === true || valid === false) {
              validation.valids.push(valid)
            }
            validation.errors.push(errorMessage)
            validation.payload = { ...validation.payload, ...payload }
            resolve('Returned')
          },
          { event, ...options }
        )
      } else {
        resolve()
      }
    })
  }

  Promise.all(promises).then((values) => {
    callback(validation)
  })
}

export const MASK = {
  onlyNumbers: (value, props = {}) => {
    //Tests that the value contains only numbers, and returns the value minus letters if not
    if (value === '') {
      return value
    } else {
      let sanitizedValue = value
      if (props.allowDecimal && value.includes('.')) {
        let segments = value.split('.')
        if (props.commas) {
          segments[0] = segments[0].replace(/\B(?=(\d{3})+(?!\d))/g, ',')
          sanitizedValue = `${segments[0]}.${segments.slice(1).join('')}`
        } else {
          segments = segments.map((seg) => seg.replace(/\D+/g, ''))
          sanitizedValue = `${segments[0].replace(/\D+/g, '')}.${segments.slice(1).join('')}`
        }
      } else {
        sanitizedValue = value.replace(/\D+/g, '')
        if (props.leadingZerosAllowed === false) {
          sanitizedValue = parseInt(sanitizedValue === '' ? 0 : sanitizedValue, 10).toString()
        }
        if (props.commas) {
          sanitizedValue = sanitizedValue.replace(/\B(?=(\d{3})+(?!\d))/g, ',')
        }
      }
      return sanitizedValue
    }
  },
  phone: (value, props) => {
    let x = value.replace(/\D/g, '').match(/(\d{0,3})(\d{0,3})(\d{0,4})/)
    value = !x[2] ? x[1] : '(' + x[1] + ') ' + x[2] + (x[3] ? '-' + x[3] : '')
    return value
  },
  social: (value, props) => {
    var val = value.replace(/\D/g, '')
    var newVal = ''
    if (val.length > 4) {
      value = val
    }
    if (val.length > 3 && val.length < 6) {
      newVal += val.substr(0, 3) + '-'
      val = val.substr(3)
    }
    if (val.length > 5) {
      newVal += val.substr(0, 3) + '-'
      newVal += val.substr(3, 2) + '-'
      val = val.substr(5)
    }
    newVal += val
    value = newVal.substring(0, 11)
    return value
  },
  fein: (value, props = {}) => {
    let val = value.replace(/\D+/g, '').split('')
    let newVal
    if (val.length > 2) {
      newVal = val.slice(0, 2).join('') + '-' + val.slice(2, 9).join('')
    } else {
      newVal = val.join('')
    }
    return newVal
  },
  currency: (value, props = {}) => {
    let hasDecimal = value.split('.').length > 1
    if (value === '$' || value === '$ ' || value === ' ') {
      return ''
    }
    if (hasDecimal) {
      let segments = value.split('.')
      let extraSegements = segments.slice(2)
      let seg1 = segments[0]
        .replace(/\D/g, '')
        .replace(/$ /g, '')
        .replace(/$/g, '')
        .replace(/ /g, '')
      let seg2 = segments[1]
      if (extraSegements) {
        seg2 += extraSegements.join()
      }
      seg2 = seg2.replace(/\D/g, '').replace(/$ /g, '').replace(/$/g, '').replace(/ /g, '')
      if (props.commas) {
        seg1 = seg1.replace(/\B(?=(\d{3})+(?!\d))/g, ',')
      }
      return `$${seg1}.${seg2}`
    } else {
      let sanitized = value
        .replace(/\D/g, '')
        .replace(/$ /g, '')
        .replace(/$/g, '')
        .replace(/ /g, '')
      if (props.commas) {
        sanitized = sanitized.replace(/\B(?=(\d{3})+(?!\d))/g, ',')
      }
      return `$${sanitized}`
    }
  },
  maxStringLength: (value, props = {}) => {
    return value.slice(0, props.max)
  },
  zipCode: (value, props = {}) => {
    let val = value.replace(/\D+/g, '').split('')
    if (val.length > 5) {
      val = val.slice(0, 5).join('') + '-' + val.slice(5, 9).join('')
    } else {
      val = val.join('')
    }
    return val
  },
  addressZipCode: (value, props = {}) => {
    let newVal = { ...value }
    newVal.POSTAL_CODE = MASK.zipCode(newVal.POSTAL_CODE)
    return newVal
  },
  onlyAscii: (value, props = {}) => {
    if (value === null || value === undefined) value = ''
    if (typeof value.val1 === 'string' || typeof value.val2 === 'string') {
      return MASK.onlyAsciiConfirmFields(value, props)
    } else if (
      typeof value.FIRST_NAME === 'string' ||
      typeof value.MIDDLE_NAME === 'string' ||
      typeof value.LAST_NAME === 'string' ||
      typeof value.SUFFIX === 'string'
    ) {
      return MASK.onlyAsciiFullName(value, props)
    } else if (
      typeof value.ADDR1 === 'string' ||
      typeof value.COUNTRY === 'string' ||
      typeof value.STATE === 'string'
    ) {
      return MASK.onlyAsciiAddress(value, props)
    } else if (Array.isArray(value)) {
      return MASK.onlyAsciiSignatures(value, props)
    } else return value.replace(/[^\x20-\x7e\r\n]/g, '')
  },
  onlyAsciiSignatures: (value, props = {}) => {
    return value.map((valObj) => ({
      ...valObj,
      sig: valObj.sig ? MASK.onlyAscii(valObj.sig, props) : '',
      capacity: valObj.capacity ? MASK.onlyAscii(valObj.capacity, props) : '',
      name: valObj.name ? MASK.onlyAscii(valObj.name, props) : '',
      behalf: valObj.behalf ? MASK.onlyAscii(valObj.behalf, props) : '',
    }))
  },
  onlyAsciiConfirmFields: (value, props = {}) => {
    return {
      ...value,
      val1: MASK.onlyAscii(value.val1, props),
      val2: MASK.onlyAscii(value.val2, props),
    }
  },
  onlyAsciiAddress: (value, props = {}) => {
    return {
      ...value,
      ADDR1: MASK.onlyAscii(value.ADDR1, props),
      ADDR2: MASK.onlyAscii(value.ADDR2, props),
      ADDR3: MASK.onlyAscii(value.ADDR3, props),
      CITY: MASK.onlyAscii(value.CITY, props),
      COUNTRY: MASK.onlyAscii(value.COUNTRY, props),
      COUNTY: MASK.onlyAscii(value.COUNTY, props),
      POSTAL_CODE: MASK.onlyAscii(value.POSTAL_CODE, props),
      STATE: value.STATE,
      props,
    }
  },
  onlyAsciiFullName: (value, props = {}) => {
    return {
      ...value,
      FIRST_NAME: MASK.onlyAscii(value.FIRST_NAME, props),
      MIDDLE_NAME: MASK.onlyAscii(value.MIDDLE_NAME, props),
      LAST_NAME: MASK.onlyAscii(value.LAST_NAME, props),
      SUFFIX: MASK.onlyAscii(value.SUFFIX || '', props),
      ORG_NAME: MASK.onlyAscii(value.ORG_NAME || '', props),
    }
  },
  allUpperCase: (value = '', props = {}) => {
    if (value && typeof value === 'object') {
      if (value.val1 !== undefined && value.reservedName === undefined) {
        return MASK.confirmFieldUppercase(value, props)
      }
      if (value.reservedName !== undefined) {
        return MASK.entityNameUppercase(value, props)
      }
      if (value.FIRST_NAME !== undefined) {
        return MASK.fullNameUppercase(value, props)
      }
    } else {
      return (value === null ? '' : value).toUpperCase()
    }
  },
  confirmFieldUppercase: (value, props = {}) => {
    let newVal = { ...value }
    newVal.val1 = MASK.allUpperCase(newVal.val1 || '')
    newVal.val2 = MASK.allUpperCase(newVal.val2 || '')
    return newVal
  },
  fullNameUppercase: (value, props = {}) => {
    let newVal = { ...value }
    newVal.FIRST_NAME = MASK.allUpperCase(newVal.FIRST_NAME)
    newVal.LAST_NAME = MASK.allUpperCase(newVal.LAST_NAME)
    newVal.MIDDLE_NAME = MASK.allUpperCase(newVal.MIDDLE_NAME)
    newVal.SUFFIX = MASK.allUpperCase(newVal.SUFFIX)
    newVal.ORG_NAME = MASK.allUpperCase(newVal.ORG_NAME)
    return newVal
  },
  entityNameUppercase: (value, props = {}) => {
    let newVal = { ...value }
    newVal.reservedName = MASK.allUpperCase(newVal.reservedName)
    newVal.val1 = MASK.allUpperCase(newVal.val1)
    newVal.val2 = MASK.allUpperCase(newVal.val2)
    return newVal
  },
  addressUpperCase: (value, props = {}) => {
    let newVal = { ...value }
    newVal.ADDR1 = MASK.allUpperCase(newVal.ADDR1)
    newVal.ADDR2 = MASK.allUpperCase(newVal.ADDR2)
    newVal.ADDR3 = MASK.allUpperCase(newVal.ADDR3)
    newVal.CITY = MASK.allUpperCase(newVal.CITY)
    newVal.POSTAL_CODE = MASK.allUpperCase(newVal.POSTAL_CODE)
    if (newVal.MAIL_ADDR) {
      newVal.MAIL_ADDR.ADDR1 = MASK.allUpperCase(newVal.MAIL_ADDR.ADDR1)
      newVal.MAIL_ADDR.ADDR2 = MASK.allUpperCase(newVal.MAIL_ADDR.ADDR2)
      newVal.MAIL_ADDR.ADDR3 = MASK.allUpperCase(newVal.MAIL_ADDR.ADDR3)
      newVal.MAIL_ADDR.CITY = MASK.allUpperCase(newVal.MAIL_ADDR.CITY)
      newVal.MAIL_ADDR.POSTAL_CODE = MASK.allUpperCase(newVal.MAIL_ADDR.POSTAL_CODE)
    }
    return newVal
  },
  time: (value, props = {}) => {
    const splitValue = value.split(' ')
    const time = splitValue[0].replace(':', '')
    const period = splitValue[1]
    // const masked = time.replace(/^(0?[1-9]|1[012]):[0-5][0-9]$/g, '')
    let masked = ''
    if (time[0] == '0' || time[0] == '1') {
      masked += time[0]
    } else {
      masked += ''
    }
    if (time[0] == '0') {
      masked += ['1', '2', '3', '4', '5', '6', '7', '8', '9'].includes(time[1]) ? time[1] : ''
    }
    if (time[0] == '1') {
      masked += ['1', '2'].includes(time[1]) ? time[1] : ''
    }
    if (masked.length === 2) {
      masked += ['0', '1', '2', '3', '4', '5'].includes(time[2]) ? ':' + time[2] : ''
    }
    if (masked.length === 4) {
      masked += ['1', '2', '3', '4', '5', '6', '7', '8', '9', '0'].includes(time[3]) ? time[3] : ''
    }

    return props.justTime ? masked : `${masked} ${period}`
  },
}

export const maskValue = (value, mask) => {
  for (var i = 0; i < mask.length; i++) {
    value = MASK[mask[i].fn](value, mask[i].props)
  }
  return value
}
